import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { doc, updateDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { copyBusinessAssessmentToLead, copyAllAssessmentDataToLead } from '../services/leadAssessmentService';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useNavigate, useLocation } from 'react-router-dom';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';
import DataDebugger from '../components/DataDebugger';
import { getPortalEmoji } from '../utils/portalEmojis';
import {
  AssessmentAlert,
  AssessmentPanel,
  AssessmentProgress,
  CompletedNote,
  FieldCard,
  GradePicker,
  HaveItCard,
  OptionCard,
  PercentDial,
  RatingScale,
  UnitAmountField,
  UploadCard,
  HOME_IMPROVEMENT_FOCUS_EMOJI,
  SUBCONTRACTOR_FOCUS_EMOJI,
} from '../components/start/assessmentUI';

/** First question: which side of the trade the lead is on. */
const CONTRACTOR_TYPE_OPTIONS = [
  {
    value: 'General Contractor',
    emoji: '🏗️',
    description: 'You manage entire construction projects and coordinate with multiple subcontractors',
  },
  {
    value: 'Subcontractor',
    emoji: '🔧',
    description: 'You specialize in specific trades and work under general contractors',
  },
];

const PROJECT_TYPE_OPTIONS = [
  { value: 'residential', emoji: '🏠', label: 'Residential Only' },
  { value: 'commercial', emoji: '🏢', label: 'Commercial Only' },
  { value: 'both', emoji: '🏘️', label: 'Both' },
];

const PROJECT_FOCUS_OPTIONS = [
  { value: 'remodels', emoji: '🔨', label: 'Remodels' },
  { value: 'newConstruction', emoji: '🧱', label: 'New Construction' },
  { value: 'both', emoji: '🏗️', label: 'Both' },
];

const CLIENT_TYPE_OPTIONS = [
  { value: 'homeowners', emoji: '🏡', label: 'Homeowners', description: 'We work directly with homeowners' },
  { value: 'gcs', emoji: '👷', label: 'General Contractors', description: 'We work under general contractors' },
  { value: 'both', emoji: '🤝', label: "Homeowners & GC's", description: 'We work with both homeowners and GCs' },
];

/**
 * Eased page scroll. The browser's native `behavior: 'smooth'` snaps too
 * quickly over the long distances between assessment sections — and pairing it
 * with an instant jump to 0 first made it read as a lurch — so we drive the
 * animation ourselves with a slow ease-in-out.
 */
const SCROLL_DURATION_MS = 900;

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const smoothScrollWindowTo = (targetY, duration = SCROLL_DURATION_MS) => {
  const startY = window.scrollY || document.documentElement.scrollTop || 0;
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const endY = Math.min(Math.max(0, targetY), maxY);
  const distance = endY - startY;
  if (Math.abs(distance) < 2) return;

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, endY);
    return;
  }

  const startedAt = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - startedAt) / duration);
    window.scrollTo(0, startY + distance * easeInOutCubic(t));
    if (t < 1) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
};

const GC_RELATIONSHIP_OPTIONS = [
  { value: 'one', emoji: '1️⃣', label: 'One General Contractor', description: 'We work primarily with one GC' },
  { value: 'multiple', emoji: '🔢', label: 'Multiple General Contractors', description: 'We work with multiple GCs' },
];

/**
 * Titled wrapper around the shared 1–5 emoji scale. Only the non-onboarding
 * fallbacks render this; the onboarding flow uses RatingScale directly inside a
 * panel that already carries the question.
 */
const StarRating = ({ rating, onRatingChange, title, description }) => (
  <div className="space-y-5">
    <div className="text-center">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
    <RatingScale value={rating} onChange={onRatingChange} />
  </div>
);

const ExistingConditions = ({ onboardingStatus, updateOnboardingStep, goToNextStep, goToPreviousStep, currentStep }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { onboardingProgress } = useOnboarding();
  const { setIsCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // The assessment wants the full width — collapse the Start sidebar on arrival
  // (it stays collapsed if they open it back up mid-assessment).
  useEffect(() => {
    setIsCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [formData, setFormData] = useState({
    contractorType: '',
    designServices: '', // yes/no
    projectType: '', // residential/commercial/both
    projectFocusType: [], // array of selected project focus types: newConstruction/remodels
    residentialPercentage: 50, // only used if projectType is 'both'
    designFeePercentage: 10, // only used if designServices is outsourced or inhouse
    // Subcontractor/Home Improvement specific fields
    clientTypes: '', // homeowners/gcs/both (used by Home Improvement, not Subcontractor)
    homeownersPercentage: 50, // only used if clientTypes is 'both'
    gcRelationship: '', // one/multiple - only used if clientTypes is 'gcs' or 'both'
    subcontractorFocus: [], // array of selected subcontractor focus areas
    homeImprovementFocus: [], // array of selected home improvement focus areas
    preferredHomeImprovementFocus: '', // string
    mostCommonHomeImprovementFocus: '', // string
    residentialFocus: [], // array of selected residential project types
    commercialFocus: [], // array of selected commercial project types
    preferredResidential: '', // string (value from residential focus list)
    preferredCommercial: '', // string (value from commercial focus list)
    mostCommonResidential: '', // string (value from residential focus list)
    mostCommonCommercial: '', // string (value from commercial focus list)
    // Common rating fields for all contractor types
    salesRating: 0,
    operationsRating: 0,
    financeRating: 0,
    marketingRating: 0,
    techRating: 0,
    // New sales-specific fields
    referralBusinessPercentage: 50,
    repeatCustomerPercentage: 50,
    closeRatio: 25, // percentage of leads that convert to sales
    salesmanCount: 0, // number of dedicated salesmen
    realtorPartners: 0,
    architectPartners: 0,
    designerPartners: 0,
    awardsWon: 0,
    // Operations-specific fields
    hasSOPs: '', // yes/no
    sopDetailLevel: '', // basic/detailed/comprehensive
    sopsFollowedCompanyWide: '', // yes/no
    hasJobDescriptions: '', // yes/no
    hasKPIs: '', // yes/no
    kpisTriggerBonuses: '', // yes/no
    kpiMonitoringFrequency: '', // weekly/monthly/quarterly/annually
    companyCultureRating: 0, // 1-5 star rating for company culture
    teamSize: '', // number of employees
    teamGrade: '', // A, B, C, D, or F
    cultureGrade: '', // A, B, C, D, or F
    workSubbedOutPercentage: 0, // percentage of work subbed out (0-100)
    spoilageGrippagePercentage: 0, // slider -30% to +30%
    chargesForChangeOrders: '', // yes/no
    markupsChangeOrders: '', // yes/no
    // Finance-specific fields
    accountingManager: '', // who manages accounting
    accountingManagerOther: '', // other accounting manager description
    accountingProcesses: '', // text description of accounting processes
    hasMonthlyReconciliation: '', // yes/no
    hasCashFlowProjection: '', // yes/no
    hasProjectBudgetsVsActuals: '', // yes/no
    hasDebt: '', // yes/no
    hasFinancialPartners: '', // yes/no
    hasCreditLine: '', // yes/no
    knowsOverhead: '', // yes/no - Do you know your overhead?
    knowsLaborBurden: '', // yes/no - Do you know your labor burden?
    knowsProjectRunway: '', // yes/no - Do you know your project runway?
    bookkeepingManager: '', // who manages bookkeeping
    bookkeepingManagerOther: '', // other bookkeeping manager description
    financialReviewFrequency: '', // frequency of financial review
    financialReviewFrequencyOther: '', // other frequency description
    monthlyOverhead: '', // monthly overhead costs
    unencumberedCash: '', // current cash position
    grossProfitMargin: '', // gross profit margin percentage
    netProfitMargin: '', // net profit margin percentage
    incomeStatementUrl: '', // income statement file URL
    incomeStatementFileName: '', // income statement file name
    balanceSheetUrl: '', // balance sheet file URL
    balanceSheetFileName: '', // balance sheet file name
    cashFlowStatementUrl: '', // cash flow statement file URL
    cashFlowStatementFileName: '', // cash flow statement file name
    // Marketing-specific fields
    annualMarketingBudget: '', // current annual marketing budget
    monthlyMarketingBudget: '', // monthly marketing budget range
    workingWithMarketingAgency: '', // yes/no
    marketingAgencyDescription: '', // description if yes
    runningDigitalAds: '', // yes/no
    digitalAdPlatforms: [], // array of selected platforms
    digitalAdPlatformsOther: '', // other platform description
    marketingPlatforms: [], // array of selected marketing platforms
    websiteManager: '', // who manages website
    marketingAgencyType: '', // In House or External
    engagesSocialMedia: '', // yes/no
    engagesPaidAds: '', // yes/no
    engagesNewsletter: '', // yes/no
    leadGenerationMethods: '', // text description of lead generation
    costPerLead: '', // current cost per lead
    // Technology-specific fields
    techInnovationLeader: '', // who leads tech innovation
    techInnovationLeaderOther: '', // other tech innovation leader description
    companyTechnicalLevel: '', // how technical the company is
    crmOptimization: 0, // how optimized the CRM is (0-100)
    whatCRM: '', // what CRM they use
    chatgptFeatures: [], // array of selected ChatGPT features
    chatgptFeaturesOther: '', // other ChatGPT features description
    hasAutomations: '', // yes/no (for new section)
    hasAITools: '', // yes/no
    hasWebsitePlugins: '', // yes/no (renamed from hasCustomWebsitePlugins)
    hasCustomWebsitePlugins: '', // yes/no (keep for backward compatibility)
    automationCount: 0, // number of automations
    // Mission, Vision, Values fields
    mission: '', // company mission statement
    vision: '', // company vision statement
    values: [], // array of company values
  });

  const [progress, setProgress] = useState(0);
  const [revenueCompleted, setRevenueCompleted] = useState(false);
  const [goalsCompleted, setGoalsCompleted] = useState(false);
  const [gpmCompleted, setGpmCompleted] = useState(false);
  const [showGoalsPopup, setShowGoalsPopup] = useState(false);
  const [showGPMPopup, setShowGPMPopup] = useState(false);
  const [showOrgChartPopup, setShowOrgChartPopup] = useState(false);
  const [showNPMPopup, setShowNPMPopup] = useState(false);
  const [showLeadsPopup, setShowLeadsPopup] = useState(false);
  const [showTechStackPopup, setShowTechStackPopup] = useState(false);
  const [showMVVPopup, setShowMVVPopup] = useState(false);
  const [showMonthlyReconciliationTooltip, setShowMonthlyReconciliationTooltip] = useState(false);
  const [showCashFlowProjectionTooltip, setShowCashFlowProjectionTooltip] = useState(false);
  const [showProjectBudgetsVsActualsTooltip, setShowProjectBudgetsVsActualsTooltip] = useState(false);
  const [uploadingIncomeStatement, setUploadingIncomeStatement] = useState(false);
  const [uploadingBalanceSheet, setUploadingBalanceSheet] = useState(false);
  const [uploadingCashFlowStatement, setUploadingCashFlowStatement] = useState(false);
  const [crmSoftwareImages, setCrmSoftwareImages] = useState({});
  const [crmImageErrors, setCrmImageErrors] = useState(new Set());
  const [showProjectTypeError, setShowProjectTypeError] = useState(false);
  const [showProjectFocusError, setShowProjectFocusError] = useState(false);
  const [showSalesRatingError, setShowSalesRatingError] = useState(false);
  const [showOperationsRatingError, setShowOperationsRatingError] = useState(false);
  const [goalsData, setGoalsData] = useState({
    annualRevenueTarget: '',
    closingRateTarget: '',
    customGoals: []
  });
  const [revenueData, setRevenueData] = useState({
    oneYearAgo: '',
    currentYearProjection: '',
    nextYearGoal: ''
  });
  const [gpmData, setGpmData] = useState({
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: ''
  });
  const [npmData, setNpmData] = useState({
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: ''
  });
  const [leadsData, setLeadsData] = useState({
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: ''
  });
  const [techStackData, setTechStackData] = useState({
    crm: ''
  });
  const [mvvData, setMvvData] = useState({
    mission: '',
    vision: '',
    values: []
  });
  const [newValue, setNewValue] = useState('');
  const [orgChartData, setOrgChartData] = useState({
    employees: []
  });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    title: '',
    reportsTo: '',
    tenure: '',
    grade: 'C',
    salary: ''
  });
  const [showOperationsGoalsPopup, setShowOperationsGoalsPopup] = useState(false);
  const [operationsGoalsData, setOperationsGoalsData] = useState({
    grossProfitMarginTarget: '',
    slippagePercentageTarget: '',
    customGoals: []
  });
  const [operationsGoalsCompleted, setOperationsGoalsCompleted] = useState(false);
  const [showMarketingGoalsPopup, setShowMarketingGoalsPopup] = useState(false);
  const [marketingGoalsData, setMarketingGoalsData] = useState({
    monthlyLeadsTarget: '',
    costPerLeadTarget: '',
    customGoals: []
  });
  const [marketingGoalsCompleted, setMarketingGoalsCompleted] = useState(false);
  const [showFinanceGoalsPopup, setShowFinanceGoalsPopup] = useState(false);
  const [financeGoalsData, setFinanceGoalsData] = useState({
    monthlyOverheadTarget: '',
    netProfitMarginTarget: '',
    customGoals: []
  });
  const [financeGoalsCompleted, setFinanceGoalsCompleted] = useState(false);
  const [showTechGoalsPopup, setShowTechGoalsPopup] = useState(false);
  const [techGoalsData, setTechGoalsData] = useState({
    activeAutomationsTarget: '',
    techStackMonthlyCostsTarget: '',
    customGoals: []
  });
  const [techGoalsCompleted, setTechGoalsCompleted] = useState(false);
  const [orgChartCompleted, setOrgChartCompleted] = useState(false);
  const [npmCompleted, setNpmCompleted] = useState(false);
  const [leadsCompleted, setLeadsCompleted] = useState(false);
  const [techStackCompleted, setTechStackCompleted] = useState(false);
  const [mvvCompleted, setMvvCompleted] = useState(false);
  const [newGoal, setNewGoal] = useState({ name: '', target: '' });
  const [showNewGoalCard, setShowNewGoalCard] = useState(false);
  const [currentSection, setCurrentSection] = useState(0); // Start on Contractor Type section (index 0)
  const [cameFromSalesRevenue, setCameFromSalesRevenue] = useState(false); // Track if user came from salesrevenue page
  const [cameFromOperationsPages, setCameFromOperationsPages] = useState(false); // Track if user came from GPMOperations or ExistingOrgChart pages
  const [cameFromNpmFinance, setCameFromNpmFinance] = useState(false); // Track if user came from npmfinance page
  const [cameFromLeadsMarketing, setCameFromLeadsMarketing] = useState(false); // Track if user came from leadsmarketing page
  const [cameFromCrmTechStack, setCameFromCrmTechStack] = useState(false); // Track if user came from crmtechstack page
  const [cameFromExistingDirection, setCameFromExistingDirection] = useState(false); // Track if user came from existingdirection page
  const mainContentRef = useRef(null);

  const sections = [
    { id: 'contractorType', title: 'Contractor Type', emoji: '🏗️' },
    { id: 'projectType', title: 'Project Type', emoji: '📐' },
    { id: 'sales', title: 'Sales', emoji: getPortalEmoji('sales') },
    { id: 'operations', title: 'Operations', emoji: getPortalEmoji('operations') },
    { id: 'finance', title: 'Finance', emoji: getPortalEmoji('finance') },
    { id: 'marketing', title: 'Marketing', emoji: getPortalEmoji('marketing') },
    { id: 'tech', title: 'Technology', emoji: getPortalEmoji('technology') },
  ];

  // Subcontractor specific sections
  const subcontractorSections = [
    { id: 'contractorType', title: 'Contractor Type', emoji: '🏗️' },
    { id: 'subcontractorFocus', title: 'Focus Areas', emoji: '🧰' },
    { id: 'sales', title: 'Sales', emoji: getPortalEmoji('sales') },
    { id: 'subcontractorFocus', title: 'Operations', emoji: getPortalEmoji('operations') },
    { id: 'finance', title: 'Finance', emoji: getPortalEmoji('finance') },
    { id: 'marketing', title: 'Marketing', emoji: getPortalEmoji('marketing') },
    { id: 'tech', title: 'Technology', emoji: getPortalEmoji('technology') },
  ];

  // Home Improvement specific sections
  const homeImprovementSections = [
    { id: 'contractorType', title: 'Contractor Type', emoji: '🏗️' },
    { id: 'clientTypes', title: 'Client Types', emoji: '🤝' },
    { id: 'homeImprovementFocus', title: 'Focus Areas', emoji: '🧰' },
    { id: 'sales', title: 'Sales', emoji: getPortalEmoji('sales') },
    { id: 'operations', title: 'Operations', emoji: getPortalEmoji('operations') },
    { id: 'finance', title: 'Finance', emoji: getPortalEmoji('finance') },
    { id: 'marketing', title: 'Marketing', emoji: getPortalEmoji('marketing') },
    { id: 'tech', title: 'Technology', emoji: getPortalEmoji('technology') },
  ];

  const subcontractorFocusOptions = [
    'Framing',
    'Plumbing',
    'Electrical',
    'HVAC',
    'Drywall',
    'Insulation',
    'Painting',
    'Roofing',
    'Flooring',
    'Tile',
    'Siding',
    'Cabinetry & Millwork',
    'Concrete & Foundations',
    'Landscaping',
    'Hardscaping',
    'Other',
  ];

  const homeImprovementFocusOptions = [
    'Handyman Services',
    'Kitchen Remodels',
    'Bathroom Remodels',
    'Basement Finishing',
    'Decks & Patios',
    'Pools',
    'Window & Door',
    'Siding & Exterior',
    'Metal Fabrication',
  ];

  // Progress mirrors the "Question X of Y" counter above the bar, so the number
  // and the fill can never disagree. Total varies by contractor-type flow
  // (Home Improvement has an extra Client Types step). The last step caps at 99%
  // — 100% is reserved for actually submitting the assessment.
  useEffect(() => {
    const totalPages = getCurrentSections().length || 1;
    const step = Math.min(currentSection + 1, totalPages);
    const pct = Math.round((step / totalPages) * 100);
    setProgress(pct >= 100 ? 99 : pct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, formData.contractorType]);

  // Ensure currentSection is always within bounds when sections change
  useEffect(() => {
    const currentSections = getCurrentSections();
    setCurrentSection(prev => {
      if (prev >= currentSections.length) {
        return Math.max(0, currentSections.length - 1);
      }
      return prev;
    });
  }, [formData.contractorType]);

  // Load existing data when component mounts
  useEffect(() => {
    const loadExistingData = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.businessAssessment) {
              console.log('Loading data from businessAssessment:', {
                hasCustomWebsitePlugins: userData.businessAssessment.hasCustomWebsitePlugins,
                hasAutomations: userData.businessAssessment.hasAutomations
              });
              setFormData(prev => {
                const businessAssessment = { ...userData.businessAssessment };
                
                // Handle projectType based on contractorType
                if (businessAssessment.contractorType === 'Subcontractor' && businessAssessment.projectType) {
                  // For subcontractors, projectType contains the trade (e.g., "Framing", "Plumbing")
                  // Set it to subcontractorFocus array
                  if (Array.isArray(businessAssessment.projectType)) {
                    businessAssessment.subcontractorFocus = businessAssessment.projectType;
                  } else {
                    businessAssessment.subcontractorFocus = [businessAssessment.projectType];
                  }
                  // Don't set projectType for subcontractors in formData
                  delete businessAssessment.projectType;
                }
                
                // Convert projectFocusType from string to array for UI (only for General Contractors)
                // Handle projectFocusType loading more robustly - check if it exists first
                if (businessAssessment.projectFocusType !== undefined && businessAssessment.projectFocusType !== null) {
                  if (businessAssessment.contractorType === 'General Contractor') {
                    const focusType = businessAssessment.projectFocusType;
                    if (focusType === 'Both') {
                      businessAssessment.projectFocusType = ['newConstruction', 'remodels'];
                    } else if (focusType === 'newConstruction') {
                      businessAssessment.projectFocusType = ['newConstruction'];
                    } else if (focusType === 'Remodels') {
                      businessAssessment.projectFocusType = ['remodels'];
                    } else if (typeof focusType === 'string') {
                      // Handle legacy values
                      businessAssessment.projectFocusType = [focusType];
                    } else if (Array.isArray(focusType)) {
                      // Already an array, keep it
                      businessAssessment.projectFocusType = focusType;
                    } else {
                      businessAssessment.projectFocusType = [];
                    }
                  } else if (businessAssessment.contractorType !== 'Subcontractor') {
                    // For non-subcontractors (but not General Contractor), ensure projectFocusType is an array
                    businessAssessment.projectFocusType = Array.isArray(businessAssessment.projectFocusType) 
                      ? businessAssessment.projectFocusType 
                      : [];
                  }
                } else if (businessAssessment.contractorType === 'General Contractor') {
                  // If contractorType is General Contractor but projectFocusType doesn't exist, initialize as empty array
                  businessAssessment.projectFocusType = [];
                }
                
                return {
                  ...prev,
                  ...businessAssessment
                };
              });
            }
            // Load finance data from financeData structure
            if (userData.financeData) {
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                financeRating: prev.financeRating || userData.financeData.rating || 0,
                hasMonthlyReconciliation: prev.hasMonthlyReconciliation || userData.financeData.hasMonthlyReconciliation || userData.businessAssessment?.hasMonthlyReconciliation || '',
                hasCashFlowProjection: prev.hasCashFlowProjection || userData.financeData.hasCashFlowProjection || userData.businessAssessment?.hasCashFlowProjection || '',
                hasProjectBudgetsVsActuals: prev.hasProjectBudgetsVsActuals || userData.financeData.hasProjectBudgetsVsActuals || userData.businessAssessment?.hasProjectBudgetsVsActuals || '',
                hasDebt: prev.hasDebt || userData.financeData.hasDebt || userData.businessAssessment?.hasDebt || '',
                hasFinancialPartners: prev.hasFinancialPartners || userData.financeData.hasFinancialPartners || userData.businessAssessment?.hasFinancialPartners || '',
                hasCreditLine: prev.hasCreditLine || userData.financeData.hasCreditLine || userData.businessAssessment?.hasCreditLine || '',
                knowsOverhead: prev.knowsOverhead || userData.financeData.knowsOverhead || userData.businessAssessment?.knowsOverhead || '',
                knowsLaborBurden: prev.knowsLaborBurden || userData.financeData.knowsLaborBurden || userData.businessAssessment?.knowsLaborBurden || '',
                knowsProjectRunway: prev.knowsProjectRunway || userData.financeData.knowsProjectRunway || userData.businessAssessment?.knowsProjectRunway || '',
                unencumberedCash: prev.unencumberedCash || userData.financeData.unencumberedCash || userData.businessAssessment?.unencumberedCash || '',
                grossProfitMargin: prev.grossProfitMargin || userData.financeData.grossProfitMargin || userData.businessAssessment?.grossProfitMargin || '',
                netProfitMargin: prev.netProfitMargin || userData.financeData.netProfitMargin || userData.businessAssessment?.netProfitMargin || '',
                incomeStatementUrl: prev.incomeStatementUrl || userData.financeData.incomeStatementUrl || userData.businessAssessment?.incomeStatementUrl || '',
                incomeStatementFileName: prev.incomeStatementFileName || userData.financeData.incomeStatementFileName || userData.businessAssessment?.incomeStatementFileName || '',
                balanceSheetUrl: prev.balanceSheetUrl || userData.financeData.balanceSheetUrl || userData.businessAssessment?.balanceSheetUrl || '',
                balanceSheetFileName: prev.balanceSheetFileName || userData.financeData.balanceSheetFileName || userData.businessAssessment?.balanceSheetFileName || '',
                cashFlowStatementUrl: prev.cashFlowStatementUrl || userData.financeData.cashFlowStatementUrl || userData.businessAssessment?.cashFlowStatementUrl || '',
                cashFlowStatementFileName: prev.cashFlowStatementFileName || userData.financeData.cashFlowStatementFileName || userData.businessAssessment?.cashFlowStatementFileName || ''
              }));
              
              // Load NPM data
              if (userData.financeData.npmData) {
                setNpmData(userData.financeData.npmData);
              }
            }
            
            // Load operations data
            if (userData.operationsData) {
              setFormData(prev => ({
                ...prev,
                operationsRating: prev.operationsRating || userData.operationsData.operationsRating || 0,
                hasSOPs: prev.hasSOPs || userData.operationsData.hasSOPs || '',
                sopDetailLevel: prev.sopDetailLevel || userData.operationsData.sopDetailLevel || '',
                sopsFollowedCompanyWide: prev.sopsFollowedCompanyWide || userData.operationsData.sopsFollowedCompanyWide || '',
                hasJobDescriptions: prev.hasJobDescriptions || userData.operationsData.hasJobDescriptions || '',
                hasKPIs: prev.hasKPIs || userData.operationsData.hasKPIs || '',
                kpisTriggerBonuses: prev.kpisTriggerBonuses || userData.operationsData.kpisTriggerBonuses || '',
                kpiMonitoringFrequency: prev.kpiMonitoringFrequency || userData.operationsData.kpiMonitoringFrequency || '',
                companyCultureRating: prev.companyCultureRating || userData.operationsData.companyCultureRating || 0,
                teamSize: prev.teamSize || userData.operationsData.teamSize || userData.businessAssessment?.teamSize || '',
                teamGrade: prev.teamGrade || userData.operationsData.teamGrade || userData.businessAssessment?.teamGrade || '',
                cultureGrade: prev.cultureGrade || userData.operationsData.cultureGrade || userData.businessAssessment?.cultureGrade || '',
                workSubbedOutPercentage: prev.workSubbedOutPercentage || userData.operationsData.workSubbedOutPercentage || userData.businessAssessment?.workSubbedOutPercentage || 0,
                spoilageGrippagePercentage: prev.spoilageGrippagePercentage || userData.operationsData.spoilageGrippagePercentage || 0,
                chargesForChangeOrders: prev.chargesForChangeOrders || userData.operationsData.chargesForChangeOrders || '',
                markupsChangeOrders: prev.markupsChangeOrders || userData.operationsData.markupsChangeOrders || ''
              }));
              
              // Load GPM data
              if (userData.operationsData.gpmData) {
                setGpmData(userData.operationsData.gpmData);
              }
              
              // Load org chart data
              if (userData.operationsData.orgChartData) {
                setOrgChartData(userData.operationsData.orgChartData);
              }
            }
            
            // Load sales data
            if (userData.salesData) {
              setFormData(prev => ({
                ...prev,
                salesRating: prev.salesRating || userData.salesData.salesRating || 0,
                referralBusinessPercentage: prev.referralBusinessPercentage || userData.salesData.referralBusinessPercentage || 0,
                repeatCustomerPercentage: prev.repeatCustomerPercentage || userData.salesData.repeatCustomerPercentage || 0,
                closeRatio: prev.closeRatio || userData.salesData.closeRatio || 0,
                salesmanCount: prev.salesmanCount || userData.salesData.salesmanCount || 0,
                realtorPartners: prev.realtorPartners || userData.salesData.realtorPartners || 0,
                architectPartners: prev.architectPartners || userData.salesData.architectPartners || 0,
                designerPartners: prev.designerPartners || userData.salesData.designerPartners || 0,
                awardsWon: prev.awardsWon || userData.salesData.awardsWon || 0
              }));
              
              // Load revenue data
              if (userData.salesData && userData.salesData.revenueData) {
                setRevenueData({
                  oneYearAgo: userData.salesData.revenueData.oneYearAgo || '',
                  currentYearProjection: userData.salesData.revenueData.currentYearProjection || '',
                  nextYearGoal: userData.salesData.revenueData.nextYearGoal || ''
                });
              } else if (userData.salesData && (userData.salesData.oneYearAgo !== undefined || userData.salesData.currentYearProjection !== undefined)) {
                // Handle legacy data structure (flat structure)
                setRevenueData({
                  oneYearAgo: userData.salesData.oneYearAgo || '',
                  currentYearProjection: userData.salesData.currentYearProjection || '',
                  nextYearGoal: userData.salesData.nextYearGoal || ''
                });
              }
            }
            
            // Load marketing data
            if (userData.marketingData) {
              setFormData(prev => ({
                ...prev,
                marketingRating: prev.marketingRating || userData.marketingData.marketingRating || 0,
                annualMarketingBudget: prev.annualMarketingBudget || userData.marketingData.annualMarketingBudget || '',
                monthlyMarketingBudget: prev.monthlyMarketingBudget || userData.marketingData.monthlyMarketingBudget || userData.businessAssessment?.monthlyMarketingBudget || '',
                workingWithMarketingAgency: prev.workingWithMarketingAgency || userData.marketingData.workingWithMarketingAgency || '',
                marketingAgencyDescription: prev.marketingAgencyDescription || userData.marketingData.marketingAgencyDescription || '',
                runningDigitalAds: prev.runningDigitalAds || userData.marketingData.runningDigitalAds || '',
                digitalAdPlatforms: prev.digitalAdPlatforms || userData.marketingData.digitalAdPlatforms || [],
                digitalAdPlatformsOther: prev.digitalAdPlatformsOther || userData.marketingData.digitalAdPlatformsOther || '',
                marketingPlatforms: prev.marketingPlatforms || userData.marketingData.marketingPlatforms || [],
                websiteManager: prev.websiteManager || userData.marketingData.websiteManager || '',
                marketingAgencyType: prev.marketingAgencyType || userData.marketingData.marketingAgencyType || userData.businessAssessment?.marketingAgencyType || '',
                engagesSocialMedia: prev.engagesSocialMedia || userData.marketingData.engagesSocialMedia || userData.businessAssessment?.engagesSocialMedia || '',
                engagesPaidAds: prev.engagesPaidAds || userData.marketingData.engagesPaidAds || userData.businessAssessment?.engagesPaidAds || '',
                engagesNewsletter: prev.engagesNewsletter || userData.marketingData.engagesNewsletter || userData.businessAssessment?.engagesNewsletter || '',
                leadGenerationMethods: prev.leadGenerationMethods || userData.marketingData.leadGenerationMethods || '',
                costPerLead: prev.costPerLead || userData.marketingData.costPerLead || ''
              }));
              
              // Load leads data
              if (userData.marketingData.leadsData) {
                setLeadsData(userData.marketingData.leadsData);
              }
            }
            
            // Load tech data
            if (userData.techData) {
              setFormData(prev => ({
                ...prev,
                techRating: prev.techRating || userData.techData.techRating || 0,
                techInnovationLeader: prev.techInnovationLeader || userData.techData.techInnovationLeader || '',
                techInnovationLeaderOther: prev.techInnovationLeaderOther || userData.techData.techInnovationLeaderOther || '',
                companyTechnicalLevel: prev.companyTechnicalLevel || userData.techData.companyTechnicalLevel || '',
                crmOptimization: prev.crmOptimization !== undefined ? prev.crmOptimization : (userData.techData.crmOptimization !== undefined ? userData.techData.crmOptimization : (userData.businessAssessment?.crmOptimization !== undefined ? userData.businessAssessment.crmOptimization : 0)),
                whatCRM: prev.whatCRM || userData.techData.whatCRM || userData.businessAssessment?.whatCRM || '',
                chatgptFeatures: prev.chatgptFeatures || userData.techData.chatgptFeatures || [],
                chatgptFeaturesOther: prev.chatgptFeaturesOther || userData.techData.chatgptFeaturesOther || '',
                hasAutomations: prev.hasAutomations || userData.techData.hasAutomations || userData.businessAssessment?.hasAutomations || '',
                hasAITools: prev.hasAITools || userData.techData.hasAITools || userData.businessAssessment?.hasAITools || '',
                hasWebsitePlugins: prev.hasWebsitePlugins || userData.techData.hasWebsitePlugins || userData.businessAssessment?.hasWebsitePlugins || '',
                hasCustomWebsitePlugins: prev.hasCustomWebsitePlugins || userData.techData.hasCustomWebsitePlugins || userData.businessAssessment?.hasCustomWebsitePlugins || ''
              }));
              
              // Load tech stack data
              if (userData.techData.techStackData) {
                setTechStackData(userData.techData.techStackData);
              }
            }
            // Load goals data for each assessment type
            if (userData.salesData && userData.salesData.goalsData) {
              setGoalsData(userData.salesData.goalsData);
              setGoalsCompleted(userData.salesData.goalsCompleted || false);
            }
            if (userData.operationsData && userData.operationsData.goalsData) {
              setOperationsGoalsData(userData.operationsData.goalsData);
              setOperationsGoalsCompleted(userData.operationsData.goalsCompleted || false);
            }
            if (userData.financeData && userData.financeData.goalsData) {
              setFinanceGoalsData(userData.financeData.goalsData);
              setFinanceGoalsCompleted(userData.financeData.goalsCompleted || false);
            }
            if (userData.marketingData && userData.marketingData.goalsData) {
              setMarketingGoalsData(userData.marketingData.goalsData);
              setMarketingGoalsCompleted(userData.marketingData.goalsCompleted || false);
            }
            if (userData.techData && userData.techData.goalsData) {
              setTechGoalsData(userData.techData.goalsData);
              setTechGoalsCompleted(userData.techData.goalsCompleted || false);
            }
            
            // Load progress data
            if (userData.businessAssessmentProgress) {
              setProgress(userData.businessAssessmentProgress.progress || 0);
              const savedSection = userData.businessAssessmentProgress.currentSection || 0;
              setCurrentSection(savedSection);
              
              // Check if user came from salesrevenue page (section 3 and revenue completed)
              if (savedSection === 3 && userData.businessAssessment && userData.businessAssessment.revenueCompleted) {
                setCameFromSalesRevenue(true);
              }
              
              // Check if user came from GPMOperations or ExistingOrgChart pages (section 4 and GPM or org chart completed)
              if (savedSection === 4 && (
                (userData.businessAssessment && userData.businessAssessment.gpmCompleted) ||
                (userData.businessAssessment && userData.businessAssessment.orgChartCompleted)
              )) {
                setCameFromOperationsPages(true);
              }
            }
            // Check if revenue is completed - check both businessAssessment and salesData
            if ((userData.businessAssessment && userData.businessAssessment.revenueCompleted) || 
                (userData.salesData && userData.salesData.revenueCompleted)) {
              setRevenueCompleted(true);
              // Load revenue data from salesData if available
              if (userData.salesData && userData.salesData.revenueData) {
                setRevenueData(userData.salesData.revenueData);
              }
            }
            // Check if GPM is completed - check both businessAssessment and operationsData
            if ((userData.businessAssessment && userData.businessAssessment.gpmCompleted) || 
                (userData.operationsData && userData.operationsData.gpmCompleted)) {
              setGpmCompleted(true);
              // Load GPM data from operationsData if available
              if (userData.operationsData && userData.operationsData.gpmData) {
                setGpmData(userData.operationsData.gpmData);
              }
            }
            // Check if goals are completed
            if (userData.salesData && userData.salesData.goalsCompleted) {
              setGoalsCompleted(true);
              if (userData.salesData.goalsData) {
                setGoalsData(userData.salesData.goalsData);
              }
            }
            // Check if org chart is completed - check both businessAssessment and operationsData
            if ((userData.businessAssessment && userData.businessAssessment.orgChartCompleted) || 
                (userData.operationsData && userData.operationsData.orgChartCompleted)) {
              setOrgChartCompleted(true);
              // Load org chart data from operationsData if available
              if (userData.operationsData && userData.operationsData.orgChartData) {
                setOrgChartData(userData.operationsData.orgChartData);
              }
            }
            // Check if NPM is completed - check both businessAssessment and financeData
            if ((userData.businessAssessment && userData.businessAssessment.npmCompleted) || 
                (userData.financeData && userData.financeData.npmCompleted)) {
              setNpmCompleted(true);
              // Load NPM data from financeData if available
              if (userData.financeData && userData.financeData.npmData) {
                setNpmData(userData.financeData.npmData);
              }
            }
            // Check if leads are completed - check both businessAssessment and marketingData
            if ((userData.businessAssessment && userData.businessAssessment.leadsCompleted) || 
                (userData.marketingData && userData.marketingData.leadsCompleted)) {
              setLeadsCompleted(true);
              // Load leads data from marketingData if available
              if (userData.marketingData && userData.marketingData.leadsData) {
                setLeadsData(userData.marketingData.leadsData);
              }
            }
            // Check if tech stack is completed - check both businessAssessment and techData
            if ((userData.businessAssessment && userData.businessAssessment.techStackCompleted) || 
                (userData.techData && userData.techData.techStackCompleted)) {
              setTechStackCompleted(true);
              // Load tech stack data from techData if available
              if (userData.techData && userData.techData.techStackData) {
                setTechStackData(userData.techData.techStackData);
              }
            }
            // Check if MVV is completed
            if (userData.businessAssessment && userData.businessAssessment.mvvCompleted) {
              setMvvCompleted(true);
              // Load MVV data from businessAssessment if available
              if (userData.businessAssessment.mission || userData.businessAssessment.vision || userData.businessAssessment.values) {
                setMvvData({
                  mission: userData.businessAssessment.mission || '',
                  vision: userData.businessAssessment.vision || '',
                  values: userData.businessAssessment.values || []
                });
              }
            }
            // Check if operations goals are completed
            if (userData.operationsData && userData.operationsData.goalsCompleted) {
              setOperationsGoalsCompleted(true);
              if (userData.operationsData.goalsData) {
                setOperationsGoalsData(userData.operationsData.goalsData);
              }
            }
            // Check if marketing goals are completed
            if (userData.marketingData && userData.marketingData.goalsCompleted) {
              setMarketingGoalsCompleted(true);
              if (userData.marketingData.goalsData) {
                setMarketingGoalsData(userData.marketingData.goalsData);
              }
            }
            // Check if finance goals are completed
            if (userData.financeData && userData.financeData.goalsCompleted) {
              setFinanceGoalsCompleted(true);
              if (userData.financeData.goalsData) {
                setFinanceGoalsData(userData.financeData.goalsData);
              }
            }
            // Check if tech goals are completed
            if (userData.techData && userData.techData.goalsCompleted) {
              setTechGoalsCompleted(true);
              if (userData.techData.goalsData) {
                setTechGoalsData(userData.techData.goalsData);
              }
            }
            // Load tech assessment data from techData collection if businessAssessment doesn't have it
            if (userData.techData) {
              console.log('Loading tech data from techData collection:', {
                websitePlugins: userData.techData.websitePlugins,
                hasAutomations: userData.techData.hasAutomations,
                currentFormData: {
                  hasCustomWebsitePlugins: formData.hasCustomWebsitePlugins,
                  hasAutomations: formData.hasAutomations
                }
              });
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                hasCustomWebsitePlugins: prev.hasCustomWebsitePlugins || userData.techData.websitePlugins || '',
                hasAutomations: prev.hasAutomations || userData.techData.hasAutomations || '',
                automationCount: prev.automationCount || userData.techData.automationsCount || '',
                techRating: prev.techRating || userData.techData.rating || '',
                techInnovationLeader: prev.techInnovationLeader || userData.techData.techInnovationLeader || '',
                techInnovationLeaderOther: prev.techInnovationLeaderOther || userData.techData.techInnovationLeaderOther || '',
                companyTechnicalLevel: prev.companyTechnicalLevel || userData.techData.companyTechnical || '',
                crmOptimization: prev.crmOptimization || userData.techData.crmOptimization || '',
                chatgptFeatures: prev.chatgptFeatures || userData.techData.aiFeatures || []
              }));
            }
            // Load sales data from salesData structure
            if (userData.salesData) {
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                salesRating: prev.salesRating || userData.salesData.salesRating || 0,
                referralBusinessPercentage: prev.referralBusinessPercentage || userData.salesData.referralBusinessPercentage || 50,
                repeatCustomerPercentage: prev.repeatCustomerPercentage || userData.salesData.repeatCustomerPercentage || 50,
                closeRatio: prev.closeRatio || userData.salesData.closeRatio || 25,
                salesmanCount: prev.salesmanCount || userData.salesData.salesmanCount || 0,
                realtorPartners: prev.realtorPartners || userData.salesData.realtorPartners || 0,
                architectPartners: prev.architectPartners || userData.salesData.architectPartners || 0,
                designerPartners: prev.designerPartners || userData.salesData.designerPartners || 0,
                awardsWon: prev.awardsWon || userData.salesData.awardsWon || 0
              }));
            }

            // Load marketing data from marketingData structure
            if (userData.marketingData) {
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                marketingRating: prev.marketingRating || userData.marketingData.marketingRating || 0,
                annualMarketingBudget: prev.annualMarketingBudget || userData.marketingData.annualMarketingBudget || '',
                monthlyMarketingBudget: prev.monthlyMarketingBudget || userData.marketingData.monthlyMarketingBudget || userData.businessAssessment?.monthlyMarketingBudget || '',
                workingWithMarketingAgency: prev.workingWithMarketingAgency || userData.marketingData.workingWithMarketingAgency || '',
                marketingAgencyDescription: prev.marketingAgencyDescription || userData.marketingData.marketingAgencyDescription || '',
                runningDigitalAds: prev.runningDigitalAds || userData.marketingData.runningDigitalAds || '',
                digitalAdPlatforms: prev.digitalAdPlatforms || userData.marketingData.digitalAdPlatforms || [],
                digitalAdPlatformsOther: prev.digitalAdPlatformsOther || userData.marketingData.digitalAdPlatformsOther || '',
                marketingPlatforms: prev.marketingPlatforms || userData.marketingData.marketingPlatforms || [],
                websiteManager: prev.websiteManager || userData.marketingData.websiteManager || '',
                marketingAgencyType: prev.marketingAgencyType || userData.marketingData.marketingAgencyType || userData.businessAssessment?.marketingAgencyType || '',
                engagesSocialMedia: prev.engagesSocialMedia || userData.marketingData.engagesSocialMedia || userData.businessAssessment?.engagesSocialMedia || '',
                engagesPaidAds: prev.engagesPaidAds || userData.marketingData.engagesPaidAds || userData.businessAssessment?.engagesPaidAds || '',
                engagesNewsletter: prev.engagesNewsletter || userData.marketingData.engagesNewsletter || userData.businessAssessment?.engagesNewsletter || '',
                leadGenerationMethods: prev.leadGenerationMethods || userData.marketingData.leadGenerationMethods || '',
                costPerLead: prev.costPerLead || userData.marketingData.costPerLead || ''
              }));
            }

            // Load operations data from operationsData structure
            if (userData.operationsData) {
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                operationsRating: prev.operationsRating || userData.operationsData.operationsRating || 0,
                hasSOPs: prev.hasSOPs || userData.operationsData.hasSOPs || '',
                sopDetailLevel: prev.sopDetailLevel || userData.operationsData.sopDetailLevel || '',
                sopsFollowedCompanyWide: prev.sopsFollowedCompanyWide || userData.operationsData.sopsFollowedCompanyWide || '',
                hasJobDescriptions: prev.hasJobDescriptions || userData.operationsData.hasJobDescriptions || '',
                hasKPIs: prev.hasKPIs || userData.operationsData.hasKPIs || '',
                kpisTriggerBonuses: prev.kpisTriggerBonuses || userData.operationsData.kpisTriggerBonuses || '',
                kpiMonitoringFrequency: prev.kpiMonitoringFrequency || userData.operationsData.kpiMonitoringFrequency || '',
                companyCultureRating: prev.companyCultureRating || userData.operationsData.companyCultureRating || 0,
                teamSize: prev.teamSize || userData.operationsData.teamSize || userData.businessAssessment?.teamSize || '',
                teamGrade: prev.teamGrade || userData.operationsData.teamGrade || userData.businessAssessment?.teamGrade || '',
                cultureGrade: prev.cultureGrade || userData.operationsData.cultureGrade || userData.businessAssessment?.cultureGrade || '',
                spoilageGrippagePercentage: prev.spoilageGrippagePercentage || userData.operationsData.spoilageGrippagePercentage || 0,
                chargesForChangeOrders: prev.chargesForChangeOrders || userData.operationsData.chargesForChangeOrders || '',
                markupsChangeOrders: prev.markupsChangeOrders || userData.operationsData.markupsChangeOrders || ''
              }));
            }

            // Load tech data from techData structure
            if (userData.techData) {
              setFormData(prev => ({
                ...prev,
                // Only update if businessAssessment doesn't already have the value
                techRating: prev.techRating || userData.techData.techRating || 0,
                techInnovationLeader: prev.techInnovationLeader || userData.techData.techInnovationLeader || '',
                techInnovationLeaderOther: prev.techInnovationLeaderOther || userData.techData.techInnovationLeaderOther || '',
                companyTechnicalLevel: prev.companyTechnicalLevel || userData.techData.companyTechnicalLevel || '',
                hasAutomations: prev.hasAutomations || userData.techData.hasAutomations || userData.businessAssessment?.hasAutomations || '',
                hasAITools: prev.hasAITools || userData.techData.hasAITools || userData.businessAssessment?.hasAITools || '',
                hasWebsitePlugins: prev.hasWebsitePlugins || userData.techData.hasWebsitePlugins || userData.businessAssessment?.hasWebsitePlugins || '',
                hasCustomWebsitePlugins: prev.hasCustomWebsitePlugins || userData.techData.hasCustomWebsitePlugins || userData.businessAssessment?.hasCustomWebsitePlugins || '',
                crmOptimization: prev.crmOptimization !== undefined ? prev.crmOptimization : (userData.techData.crmOptimization !== undefined ? userData.techData.crmOptimization : (userData.businessAssessment?.crmOptimization !== undefined ? userData.businessAssessment.crmOptimization : 0)),
                whatCRM: prev.whatCRM || userData.techData.whatCRM || userData.businessAssessment?.whatCRM || '',
                chatgptFeatures: prev.chatgptFeatures || userData.techData.chatgptFeatures || [],
                chatgptFeaturesOther: prev.chatgptFeaturesOther || userData.techData.chatgptFeaturesOther || ''
              }));
            }
          }
        } catch (error) {
          console.error('Error loading existing conditions:', error);
        }
      }
    };

    loadExistingData();
  }, [currentUser]);

  // Auto-scroll: to top when navigating to sales/ops/tech/finance/marketing; to bottom when returning from sub-pages
  useEffect(() => {
    const scrollToTop = () => {
      setTimeout(() => {
        const card = mainContentRef.current;
        const targetY = card
          ? card.getBoundingClientRect().top + (window.scrollY || 0) - 16
          : 0;
        smoothScrollWindowTo(targetY);
      }, 150);
    };
    if (currentSection === 2) {
      // Sales section - scroll to top
      scrollToTop();
    } else if (currentSection === 3 && cameFromSalesRevenue) {
      // Operations section - scroll to bottom when returning from salesrevenue page
      setTimeout(() => {
        smoothScrollWindowTo(document.documentElement.scrollHeight);
      }, 100);
      setCameFromSalesRevenue(false);
    } else if (currentSection === 3) {
      // Operations section - scroll to top when navigating normally
      scrollToTop();
    } else if (currentSection === 4) {
      if (cameFromOperationsPages) {
        // Finance section auto-scrolls to bottom if user came from GPMOperations or ExistingOrgChart pages
        setTimeout(() => {
          smoothScrollWindowTo(document.documentElement.scrollHeight);
        }, 100);
        // Reset the flag after scrolling
        setCameFromOperationsPages(false);
      } else {
        // Finance section - scroll to top when navigating normally
        scrollToTop();
      }
    } else if (currentSection === 5) {
      // Marketing section (index 5)
      if (cameFromNpmFinance) {
        // Marketing section auto-scrolls to bottom if user came from npmfinance page
        setTimeout(() => {
          smoothScrollWindowTo(document.documentElement.scrollHeight);
        }, 100);
        // Reset the flag after scrolling
        setCameFromNpmFinance(false);
      } else {
        // Marketing section - scroll to top when navigating normally
        scrollToTop();
      }
    } else if (currentSection === 6) {
      // Technology section (index 6)
      if (cameFromLeadsMarketing) {
        // Technology section auto-scrolls to bottom if user came from leadsmarketing page
        setTimeout(() => {
          smoothScrollWindowTo(document.documentElement.scrollHeight);
        }, 100);
        // Reset the flag after scrolling
        setCameFromLeadsMarketing(false);
      } else {
        // Technology section - scroll to top when navigating normally
        scrollToTop();
      }
    } else if (currentSection === 7) {
      // Technology section (index 7)
      if (cameFromCrmTechStack) {
        // Technology section auto-scrolls to bottom if user came from crmtechstack page
        setTimeout(() => {
          smoothScrollWindowTo(document.documentElement.scrollHeight);
        }, 100);
        // Reset the flag after scrolling
        setCameFromCrmTechStack(false);
      } else {
        scrollToTop();
      }
    } else if (currentSection === 8) {
      // HR section (index 8)
      if (cameFromExistingDirection) {
        // HR section auto-scrolls to bottom if user came from existingdirection page
        setTimeout(() => {
          smoothScrollWindowTo(document.documentElement.scrollHeight);
        }, 100);
        // Reset the flag after scrolling
        setCameFromExistingDirection(false);
      } else {
        scrollToTop();
      }
    } else if (currentSection === 9) {
      scrollToTop();
    }
  }, [currentSection, cameFromSalesRevenue, cameFromOperationsPages, cameFromNpmFinance, cameFromLeadsMarketing, cameFromCrmTechStack, cameFromExistingDirection]);

  // Fetch CRM software images from Firebase
  useEffect(() => {
    const fetchCrmSoftwareImages = async () => {
      try {
        const crmNames = ['JobTread', 'Buildertrend', 'Procore', 'HubSpot'];
        const images = {};
        
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          
          for (const typeDoc of typesSnapshot.docs) {
            const typeData = typeDoc.data();
            // Check if this is a CRM type (could be named "CRM" or similar)
            if (typeData && typeData.name && (typeData.name.toLowerCase().includes('crm') || typeData.name.toLowerCase().includes('customer'))) {
              const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
              
              softwareSnapshot.forEach(softwareDoc => {
                const data = softwareDoc.data();
                if (data && data.name && crmNames.includes(data.name)) {
                  if (data.image) {
                    images[data.name] = data.image;
                  }
                }
              });
            }
          }
        }
        
        setCrmSoftwareImages(images);
      } catch (error) {
        console.error('Error fetching CRM software images:', error);
      }
    };
    
    fetchCrmSoftwareImages();
  }, []);

  // Detect if user came from npmfinance page
  useEffect(() => {
    if (location.state?.fromNpmFinance) {
      setCameFromNpmFinance(true);
    }
  }, [location.state]);

  // Detect if user came from leadsmarketing page
  useEffect(() => {
    if (location.state?.fromLeadsMarketing) {
      setCameFromLeadsMarketing(true);
    }
  }, [location.state]);

  // Detect if user came from crmtechstack page
  useEffect(() => {
    if (location.state?.fromCrmTechStack) {
      setCameFromCrmTechStack(true);
    }
  }, [location.state]);

  // Detect if user came from existingdirection page
  useEffect(() => {
    if (location.state?.fromExistingDirection) {
      setCameFromExistingDirection(true);
    }
  }, [location.state]);

  // Detect if user came from salesrevenue page and refresh data
  useEffect(() => {
    if (location.state?.fromSalesRevenue) {
      setCameFromSalesRevenue(true);
      // Refresh user data to get updated revenue completion status
      const refreshData = async () => {
        if (currentUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Check if revenue is completed
              if (userData.businessAssessment && userData.businessAssessment.revenueCompleted) {
                setRevenueCompleted(true);
                console.log('Revenue completion status updated to true');
              }
            }
          } catch (error) {
            console.error('Error refreshing data after sales revenue:', error);
          }
        }
      };
      refreshData();
    }
  }, [location.state, currentUser]);

  // Detect if user came from operations pages (GPM or Org Chart) and refresh data
  useEffect(() => {
    if (location.state?.fromGPMOperations || location.state?.fromExistingOrgChart) {
      setCameFromOperationsPages(true);
      // Refresh user data to get updated operations completion status
      const refreshData = async () => {
        if (currentUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Check if GPM is completed
              if (userData.businessAssessment && userData.businessAssessment.gpmCompleted) {
                setGpmCompleted(true);
                console.log('GPM completion status updated to true');
              }
              // Check if org chart is completed
              if (userData.businessAssessment && userData.businessAssessment.orgChartCompleted) {
                setOrgChartCompleted(true);
                console.log('Org chart completion status updated to true');
              }
            }
          } catch (error) {
            console.error('Error refreshing data after operations pages:', error);
          }
        }
      };
      refreshData();
    }
  }, [location.state, currentUser]);

  // Set cameFrom flags immediately when component mounts to avoid timing issues
  useEffect(() => {
    if (location.state?.fromGPMOperations || location.state?.fromExistingOrgChart) {
      setCameFromOperationsPages(true);
    }
    if (location.state?.fromSalesRevenue) {
      setCameFromSalesRevenue(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Save finance assessment data immediately for finance-related fields
    const financeFields = [
      'accountingManagerOther', 
      'accountingProcesses', 
      'bookkeepingManagerOther', 
      'financialReviewFrequencyOther', 
      'monthlyOverhead', 
      'unencumberedCash'
    ];
    
    // Save marketing assessment data immediately for marketing-related fields
    const marketingFields = [
      'annualMarketingBudget',
      'marketingAgencyDescription',
      'digitalAdPlatformsOther',
      'leadGenerationMethods',
      'costPerLead'
    ];
    
    // Save tech assessment data immediately for tech-related fields
    const techFields = [
      'techInnovationLeaderOther',
      'automationCount'
    ];
    
    if (financeFields.includes(name)) {
      setTimeout(() => saveFinanceAssessmentData(), 100);
    }
    
    if (marketingFields.includes(name)) {
      setTimeout(() => saveMarketingAssessmentData(), 100);
    }
    
    if (techFields.includes(name)) {
      setTimeout(() => saveTechAssessmentData(), 100);
    }
    
    // Save progress update after form data change
    setTimeout(() => saveProgressUpdate(), 100);
  };

  // Function to save contractor type selection immediately
  const saveContractorType = async (contractorType) => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.contractorType': contractorType,
        'businessAssessmentProgress.contractorTypeCompleted': true,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving contractor type:', error);
    }
  };

  // Function to save project type data immediately
  const saveProjectTypeData = async () => {
    if (!currentUser) return;
    
    try {
      const updateData = {
        'businessAssessment.projectType': formData.projectType,
        'businessAssessmentProgress.projectTypeCompleted': true,
        lastUpdated: new Date().toISOString()
      };

      // Convert projectFocusType array to single string value (only for General Contractors)
      if (formData.contractorType === 'General Contractor') {
        let projectFocusTypeValue = '';
        if (Array.isArray(formData.projectFocusType)) {
          const hasNewConstruction = formData.projectFocusType.includes('newConstruction');
          const hasRemodels = formData.projectFocusType.includes('remodels');
          if (hasNewConstruction && hasRemodels) {
            projectFocusTypeValue = 'Both';
          } else if (hasNewConstruction) {
            projectFocusTypeValue = 'newConstruction';
          } else if (hasRemodels) {
            projectFocusTypeValue = 'Remodels';
          }
        } else if (formData.projectFocusType) {
          projectFocusTypeValue = formData.projectFocusType;
        }
        
        // Only include projectFocusType if it has a value
        if (projectFocusTypeValue) {
          updateData['businessAssessment.projectFocusType'] = projectFocusTypeValue;
        }
      }

      await updateDoc(doc(db, 'users', currentUser.uid), updateData);
    } catch (error) {
      console.error('Error saving project type data:', error);
    }
  };


  // Function to save client types data immediately
  const saveClientTypesData = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.clientTypes': formData.clientTypes,
        'businessAssessment.gcRelationship': formData.gcRelationship,
        'businessAssessment.homeownersPercentage': formData.homeownersPercentage,
        'businessAssessmentProgress.clientTypesCompleted': true,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving client types data:', error);
    }
  };

  // Function to save subcontractor focus data immediately
  const saveSubcontractorFocusData = async () => {
    if (!currentUser) return;
    
    try {
      // For subcontractors, projectType should be set to the selected focus area
      const projectTypeValue = Array.isArray(formData.subcontractorFocus) && formData.subcontractorFocus.length > 0
        ? formData.subcontractorFocus[0]
        : (formData.subcontractorFocus || '');
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.projectType': projectTypeValue,
        'businessAssessmentProgress.subcontractorFocusCompleted': true,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving subcontractor focus data:', error);
    }
  };

  // Function to save home improvement focus data immediately
  const saveHomeImprovementFocusData = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.homeImprovementFocus': formData.homeImprovementFocus,
        'businessAssessment.preferredHomeImprovementFocus': formData.preferredHomeImprovementFocus,
        'businessAssessment.mostCommonHomeImprovementFocus': formData.mostCommonHomeImprovementFocus,
        'businessAssessmentProgress.homeImprovementFocusCompleted': true,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving home improvement focus data:', error);
    }
  };

  // Function to save sales assessment data immediately
  const saveSalesAssessmentData = async () => {
    if (!currentUser) return;
    
    try {
      // Prepare data object and filter out undefined values
      const updateData = {
        'businessAssessment.salesRating': formData.salesRating,
        'businessAssessment.referralBusinessPercentage': formData.referralBusinessPercentage,
        'businessAssessment.repeatCustomerPercentage': formData.repeatCustomerPercentage,
        'businessAssessment.closeRatio': formData.closeRatio,
        'businessAssessment.salesmanCount': formData.salesmanCount,
        'businessAssessment.revenueData': revenueData,
        'businessAssessmentProgress.salesAssessmentCompleted': true,
        lastUpdated: new Date().toISOString()
      };

      // Filter out undefined values to prevent Firestore errors
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([key, value]) => value !== undefined)
      );

      await updateDoc(doc(db, 'users', currentUser.uid), filteredData);
    } catch (error) {
      console.error('Error saving sales assessment data:', error);
    }
  };

  const saveOperationsAssessmentData = async (updatedData = null) => {
    if (!currentUser) return;
    
    try {
      const dataToSave = updatedData || formData;
      
      // Prepare data object and filter out undefined values
      const updateData = {
        // Save to businessAssessment (existing structure)
        'businessAssessment.operationsRating': dataToSave.operationsRating,
        'businessAssessment.hasSOPs': dataToSave.hasSOPs,
        'businessAssessment.hasJobDescriptions': dataToSave.hasJobDescriptions,
        'businessAssessment.hasKPIs': dataToSave.hasKPIs,
        'businessAssessment.companyCultureRating': dataToSave.companyCultureRating,
        'businessAssessment.teamSize': dataToSave.teamSize,
        'businessAssessment.teamGrade': dataToSave.teamGrade,
        'businessAssessment.cultureGrade': dataToSave.cultureGrade,
        'businessAssessment.workSubbedOutPercentage': dataToSave.workSubbedOutPercentage,
        'businessAssessment.gpmData': gpmData,
        'businessAssessment.operationsGoalsData': operationsGoalsData,
        'businessAssessmentProgress.operationsAssessmentCompleted': true,
        
        // Also save to operationsData collection for debugging and separate access
        'operationsData.operationsRating': dataToSave.operationsRating,
        'operationsData.hasSOPs': dataToSave.hasSOPs,
        'operationsData.hasJobDescriptions': dataToSave.hasJobDescriptions,
        'operationsData.hasKPIs': dataToSave.hasKPIs,
        'operationsData.companyCultureRating': dataToSave.companyCultureRating,
        'operationsData.teamSize': dataToSave.teamSize,
        'operationsData.teamGrade': dataToSave.teamGrade,
        'operationsData.cultureGrade': dataToSave.cultureGrade,
        'operationsData.workSubbedOutPercentage': dataToSave.workSubbedOutPercentage,
        'operationsData.gpmData': gpmData,
        'operationsData.operationsGoalsData': operationsGoalsData,
        'operationsData.assessmentCompleted': true,
        
        lastUpdated: new Date().toISOString()
      };

      // Filter out undefined values to prevent Firestore errors
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([key, value]) => value !== undefined)
      );

      await updateDoc(doc(db, 'users', currentUser.uid), filteredData);
    } catch (error) {
      console.error('Error saving operations assessment data:', error);
    }
  };

  const saveFinanceAssessmentData = async (overrides = {}) => {
    if (!currentUser) return;
    
    try {
      const data = { ...formData, ...overrides };
      // Prepare data object and filter out undefined values
      const updateData = {
        // Save to businessAssessment (existing structure)
        'businessAssessment.financeRating': data.financeRating,
        'businessAssessment.hasMonthlyReconciliation': data.hasMonthlyReconciliation,
        'businessAssessment.hasCashFlowProjection': data.hasCashFlowProjection,
        'businessAssessment.hasProjectBudgetsVsActuals': data.hasProjectBudgetsVsActuals,
        'businessAssessment.hasDebt': data.hasDebt,
        'businessAssessment.hasFinancialPartners': data.hasFinancialPartners,
        'businessAssessment.hasCreditLine': data.hasCreditLine,
        'businessAssessment.knowsOverhead': data.knowsOverhead,
        'businessAssessment.knowsLaborBurden': data.knowsLaborBurden,
        'businessAssessment.knowsProjectRunway': data.knowsProjectRunway,
        'businessAssessment.unencumberedCash': data.unencumberedCash,
        'businessAssessment.grossProfitMargin': data.grossProfitMargin,
        'businessAssessment.netProfitMargin': data.netProfitMargin,
        'businessAssessment.incomeStatementUrl': data.incomeStatementUrl,
        'businessAssessment.incomeStatementFileName': data.incomeStatementFileName,
        'businessAssessment.balanceSheetUrl': data.balanceSheetUrl,
        'businessAssessment.balanceSheetFileName': data.balanceSheetFileName,
        'businessAssessment.cashFlowStatementUrl': data.cashFlowStatementUrl,
        'businessAssessment.cashFlowStatementFileName': data.cashFlowStatementFileName,
        'businessAssessment.npmData': npmData,
        'businessAssessment.financeGoalsData': financeGoalsData,
        'businessAssessmentProgress.financeAssessmentCompleted': true,
        
        // Also save to financeData collection for debugging and separate access
        'financeData.rating': data.financeRating,
        'financeData.hasMonthlyReconciliation': data.hasMonthlyReconciliation,
        'financeData.hasCashFlowProjection': data.hasCashFlowProjection,
        'financeData.hasProjectBudgetsVsActuals': data.hasProjectBudgetsVsActuals,
        'financeData.hasDebt': data.hasDebt,
        'financeData.hasFinancialPartners': data.hasFinancialPartners,
        'financeData.hasCreditLine': data.hasCreditLine,
        'financeData.knowsOverhead': data.knowsOverhead,
        'financeData.knowsLaborBurden': data.knowsLaborBurden,
        'financeData.knowsProjectRunway': data.knowsProjectRunway,
        'financeData.unencumberedCash': data.unencumberedCash,
        'financeData.grossProfitMargin': data.grossProfitMargin,
        'financeData.netProfitMargin': data.netProfitMargin,
        'financeData.incomeStatementUrl': data.incomeStatementUrl,
        'financeData.incomeStatementFileName': data.incomeStatementFileName,
        'financeData.balanceSheetUrl': data.balanceSheetUrl,
        'financeData.balanceSheetFileName': data.balanceSheetFileName,
        'financeData.cashFlowStatementUrl': data.cashFlowStatementUrl,
        'financeData.cashFlowStatementFileName': data.cashFlowStatementFileName,
        'financeData.npmData': npmData,
        'financeData.goalsData': financeGoalsData,
        'financeData.assessmentCompleted': true,
        
        lastUpdated: new Date().toISOString()
      };

      // Filter out undefined and null values to prevent Firestore errors, but keep empty strings and 0 values
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([key, value]) => value !== undefined && value !== null)
      );

      await updateDoc(doc(db, 'users', currentUser.uid), filteredData);
    } catch (error) {
      console.error('Error saving finance assessment data:', error);
    }
  };

  const saveMarketingAssessmentData = async () => {
    if (!currentUser) return;
    
    try {
      // Prepare data object and filter out undefined values
      const updateData = {
        // Save to businessAssessment (existing structure)
        'businessAssessment.marketingRating': formData.marketingRating,
        'businessAssessment.annualMarketingBudget': formData.annualMarketingBudget,
        'businessAssessment.monthlyMarketingBudget': formData.monthlyMarketingBudget,
        'businessAssessment.workingWithMarketingAgency': formData.workingWithMarketingAgency,
        'businessAssessment.marketingAgencyDescription': formData.marketingAgencyDescription,
        'businessAssessment.runningDigitalAds': formData.runningDigitalAds,
        'businessAssessment.digitalAdPlatforms': formData.digitalAdPlatforms,
        'businessAssessment.digitalAdPlatformsOther': formData.digitalAdPlatformsOther,
        'businessAssessment.marketingPlatforms': formData.marketingPlatforms,
        'businessAssessment.websiteManager': formData.websiteManager,
        'businessAssessment.marketingAgencyType': formData.marketingAgencyType,
        'businessAssessment.engagesSocialMedia': formData.engagesSocialMedia,
        'businessAssessment.engagesPaidAds': formData.engagesPaidAds,
        'businessAssessment.engagesNewsletter': formData.engagesNewsletter,
        'businessAssessment.leadGenerationMethods': formData.leadGenerationMethods,
        'businessAssessment.costPerLead': formData.costPerLead,
        'businessAssessment.leadsData': leadsData,
        'businessAssessment.marketingGoalsData': marketingGoalsData,
        'businessAssessmentProgress.marketingAssessmentCompleted': true,
        
        // Also save to marketingData collection for debugging and separate access
        'marketingData.rating': formData.marketingRating,
        'marketingData.annualBudget': formData.annualMarketingBudget,
        'marketingData.monthlyMarketingBudget': formData.monthlyMarketingBudget,
        'marketingData.workingWithAgency': formData.workingWithMarketingAgency,
        'marketingData.agencyDescription': formData.marketingAgencyDescription,
        'marketingData.runningDigitalAds': formData.runningDigitalAds,
        'marketingData.digitalAdPlatforms': formData.digitalAdPlatforms,
        'marketingData.digitalAdPlatformsOther': formData.digitalAdPlatformsOther,
        'marketingData.marketingPlatforms': formData.marketingPlatforms,
        'marketingData.websiteManager': formData.websiteManager,
        'marketingData.marketingAgencyType': formData.marketingAgencyType,
        'marketingData.engagesSocialMedia': formData.engagesSocialMedia,
        'marketingData.engagesPaidAds': formData.engagesPaidAds,
        'marketingData.engagesNewsletter': formData.engagesNewsletter,
        'marketingData.leadGenerationMethods': formData.leadGenerationMethods,
        'marketingData.costPerLead': formData.costPerLead,
        'marketingData.leadsData': leadsData,
        'marketingData.goalsData': marketingGoalsData,
        'marketingData.assessmentCompleted': true,
        
        lastUpdated: new Date().toISOString()
      };

      // Filter out undefined values to prevent Firestore errors
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([key, value]) => value !== undefined)
      );

      await updateDoc(doc(db, 'users', currentUser.uid), filteredData);
    } catch (error) {
      console.error('Error saving marketing assessment data:', error);
    }
  };

  const saveTechAssessmentData = async () => {
    if (!currentUser) return;
    
    try {
      // Prepare data object and filter out undefined values
      const updateData = {
        // Save to businessAssessment (existing structure)
        'businessAssessment.techRating': formData.techRating,
        'businessAssessment.techInnovationLeader': formData.techInnovationLeader,
        'businessAssessment.techInnovationLeaderOther': formData.techInnovationLeaderOther,
        'businessAssessment.companyTechnicalLevel': formData.companyTechnicalLevel,
        'businessAssessment.crmOptimization': formData.crmOptimization,
        'businessAssessment.whatCRM': formData.whatCRM,
        'businessAssessment.chatgptFeatures': formData.chatgptFeatures,
        'businessAssessment.hasAutomations': formData.hasAutomations,
        'businessAssessment.hasAITools': formData.hasAITools,
        'businessAssessment.hasWebsitePlugins': formData.hasWebsitePlugins,
        'businessAssessment.automationCount': formData.automationCount,
        'businessAssessment.hasCustomWebsitePlugins': formData.hasCustomWebsitePlugins,
        'businessAssessment.techStackData': techStackData,
        'businessAssessment.techGoalsData': techGoalsData,
        'businessAssessmentProgress.techAssessmentCompleted': true,
        
        // Also save to techData collection for debugging and separate access
        'techData.rating': formData.techRating,
        'techData.techInnovationLeader': formData.techInnovationLeader,
        'techData.techInnovationLeaderOther': formData.techInnovationLeaderOther,
        'techData.companyTechnical': formData.companyTechnicalLevel,
        'techData.crmOptimization': formData.crmOptimization,
        'techData.whatCRM': formData.whatCRM,
        'techData.aiFeatures': formData.chatgptFeatures,
        'techData.hasAutomations': formData.hasAutomations,
        'techData.hasAITools': formData.hasAITools,
        'techData.hasWebsitePlugins': formData.hasWebsitePlugins,
        'techData.automationsCount': formData.automationCount,
        'techData.websitePlugins': formData.hasCustomWebsitePlugins,
        'techData.techStackData': techStackData,
        'techData.goalsData': techGoalsData,
        'techData.assessmentCompleted': true,
        
        lastUpdated: new Date().toISOString()
      };

      // Filter out undefined values to prevent Firestore errors
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([key, value]) => value !== undefined)
      );

      await updateDoc(doc(db, 'users', currentUser.uid), filteredData);
    } catch (error) {
      console.error('Error saving tech assessment data:', error);
    }
  };

  // saveHRAssessmentData - REMOVED (HR section removed)

  // saveOverallAssessmentData - REMOVED (Overall section removed)

  // Goals popup handlers
  const handleGoalsDataChange = (e) => {
    const { name, value } = e.target;
    setGoalsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRevenueDataChange = (e) => {
    const { name, value } = e.target;
    setRevenueData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGpmDataChange = (e) => {
    const { name, value } = e.target;
    setGpmData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNpmDataChange = (e) => {
    const { name, value } = e.target;
    setNpmData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLeadsDataChange = (e) => {
    const { name, value } = e.target;
    setLeadsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTechStackDataChange = (e) => {
    const { name, value } = e.target;
    setTechStackData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMVVDataChange = (e) => {
    const { name, value } = e.target;
    setMvvData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewValueChange = (e) => {
    setNewValue(e.target.value);
  };

  const addValue = () => {
    if (newValue.trim()) {
      setMvvData(prev => ({
        ...prev,
        values: [...prev.values, { id: Date.now().toString(), text: newValue.trim() }]
      }));
      setNewValue('');
    }
  };

  const removeValue = (id) => {
    setMvvData(prev => ({
      ...prev,
      values: prev.values.filter(value => value.id !== id)
    }));
  };

  const handleOrgChartDataChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOrgChartData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNewEmployeeChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditEmployeeChange = (e) => {
    const { name, value } = e.target;
    setEditingEmployee(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addEmployee = () => {
    if (newEmployee.name.trim() && newEmployee.title.trim()) {
      const employee = {
        id: Date.now().toString(),
        ...newEmployee,
        salary: newEmployee.salary || '0'
      };
      setOrgChartData(prev => ({
        ...prev,
        employees: [...prev.employees, employee]
      }));
      setNewEmployee({
        name: '',
        title: '',
        reportsTo: '',
        tenure: '',
        grade: 'C',
        salary: ''
      });
    }
  };

  const editEmployee = (id) => {
    const employee = orgChartData.employees.find(emp => emp.id === id);
    if (employee) {
      setEditingEmployee({ ...employee });
    }
  };

  const saveEditEmployee = () => {
    if (editingEmployee && editingEmployee.name.trim() && editingEmployee.title.trim()) {
      setOrgChartData(prev => ({
        ...prev,
        employees: prev.employees.map(emp => 
          emp.id === editingEmployee.id ? editingEmployee : emp
        )
      }));
      setEditingEmployee(null);
    }
  };

  const deleteEmployee = (id) => {
    setOrgChartData(prev => ({
      ...prev,
      employees: prev.employees.filter(emp => emp.id !== id)
    }));
  };

  const handleNewGoalChange = (e) => {
    const { name, value } = e.target;
    setNewGoal(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Operations goals popup handlers
  const handleOperationsGoalsDataChange = (e) => {
    const { name, value } = e.target;
    setOperationsGoalsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOperationsNewGoalChange = (e) => {
    const { name, value } = e.target;
    setNewGoal(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addCustomGoal = () => {
    if (newGoal.name && newGoal.name.trim() && newGoal.target && newGoal.target.trim()) {
      setGoalsData(prev => ({
        ...prev,
        customGoals: [...prev.customGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({ name: '', target: '' });
      setShowNewGoalCard(false);
    }
  };

  const showAddGoalCard = () => {
    setShowNewGoalCard(true);
  };

  const removeCustomGoal = (id) => {
    setGoalsData(prev => ({
      ...prev,
      customGoals: prev.customGoals.filter(goal => goal.id !== id)
    }));
  };

  // Operations goals functions
  const addOperationsCustomGoal = () => {
    if (newGoal.name && newGoal.name.trim() && newGoal.target && newGoal.target.trim()) {
      setOperationsGoalsData(prev => ({
        ...prev,
        customGoals: [...prev.customGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({ name: '', target: '' });
      setShowNewGoalCard(false);
    }
  };

  const removeOperationsCustomGoal = (id) => {
    setOperationsGoalsData(prev => ({
      ...prev,
      customGoals: prev.customGoals.filter(goal => goal.id !== id)
    }));
  };

  const saveGoals = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'salesData.goalsCompleted': true,
        'salesData.goalsData': goalsData,
        'businessAssessmentProgress.goalsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setGoalsCompleted(true);
      setShowGoalsPopup(false);
    } catch (error) {
      console.error('Error saving goals:', error);
      alert('Error saving goals. Please try again.');
    }
  };

  const saveRevenue = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'salesData.revenueData': revenueData,
        'businessAssessment.revenueData': revenueData,
        'businessAssessmentProgress.revenueCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setRevenueCompleted(true);
    } catch (error) {
      console.error('Error saving revenue data:', error);
    }
  };

  const saveGPM = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'operationsData.gpmCompleted': true,
        'operationsData.gpmData': gpmData,
        'businessAssessmentProgress.gpmCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setGpmCompleted(true);
      setShowGPMPopup(false);
    } catch (error) {
      console.error('Error saving GPM data:', error);
      alert('Error saving GPM data. Please try again.');
    }
  };

  const saveNPM = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'financeData.npmCompleted': true,
        'financeData.npmData': npmData,
        'businessAssessmentProgress.npmCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setNpmCompleted(true);
      setShowNPMPopup(false);
    } catch (error) {
      console.error('Error saving NPM data:', error);
      alert('Error saving NPM data. Please try again.');
    }
  };

  const saveLeads = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'marketingData.leadsCompleted': true,
        'marketingData.leadsData': leadsData,
        'businessAssessmentProgress.leadsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setLeadsCompleted(true);
      setShowLeadsPopup(false);
    } catch (error) {
      console.error('Error saving leads data:', error);
      alert('Error saving leads data. Please try again.');
    }
  };

  const saveTechStack = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'techData.techStackCompleted': true,
        'techData.techStackData': techStackData,
        'businessAssessmentProgress.techStackCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setTechStackCompleted(true);
      setShowTechStackPopup(false);
    } catch (error) {
      console.error('Error saving tech stack data:', error);
      alert('Error saving tech stack data. Please try again.');
    }
  };

  const saveMVV = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessment.mvvCompleted': true,
        'businessAssessment.mission': mvvData.mission,
        'businessAssessment.vision': mvvData.vision,
        'businessAssessment.values': mvvData.values,
        'businessAssessmentProgress.mvvCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setMvvCompleted(true);
      setShowMVVPopup(false);
    } catch (error) {
      console.error('Error saving MVV data:', error);
      alert('Error saving MVV data. Please try again.');
    }
  };

  const saveOrgChart = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'operationsData.orgChartCompleted': true,
        'operationsData.orgChartData': orgChartData,
        'businessAssessmentProgress.orgChartCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setOrgChartCompleted(true);
      setShowOrgChartPopup(false);
    } catch (error) {
      console.error('Error saving org chart data:', error);
      alert('Error saving org chart data. Please try again.');
    }
  };

  const saveOperationsGoals = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'operationsData.goalsCompleted': true,
        'operationsData.goalsData': operationsGoalsData,
        'businessAssessmentProgress.operationsGoalsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setOperationsGoalsCompleted(true);
      setShowOperationsGoalsPopup(false);
    } catch (error) {
      console.error('Error saving operations goals:', error);
      alert('Error saving operations goals. Please try again.');
    }
  };

  // Marketing goals popup handlers
  const handleMarketingGoalsDataChange = (e) => {
    const { name, value } = e.target;
    setMarketingGoalsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addMarketingCustomGoal = () => {
    if (newGoal.name && newGoal.name.trim() && newGoal.target && newGoal.target.trim()) {
      setMarketingGoalsData(prev => ({
        ...prev,
        customGoals: [...prev.customGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({ name: '', target: '' });
      setShowNewGoalCard(false);
    }
  };

  const removeMarketingCustomGoal = (id) => {
    setMarketingGoalsData(prev => ({
      ...prev,
      customGoals: prev.customGoals.filter(goal => goal.id !== id)
    }));
  };

  const saveMarketingGoals = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'marketingData.goalsCompleted': true,
        'marketingData.goalsData': marketingGoalsData,
        'businessAssessmentProgress.marketingGoalsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setMarketingGoalsCompleted(true);
      setShowMarketingGoalsPopup(false);
    } catch (error) {
      console.error('Error saving marketing goals:', error);
      alert('Error saving marketing goals. Please try again.');
    }
  };

  // Finance goals popup handlers
  const handleFinanceGoalsDataChange = (e) => {
    const { name, value } = e.target;
    setFinanceGoalsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addFinanceCustomGoal = () => {
    if (newGoal.name && newGoal.name.trim() && newGoal.target && newGoal.target.trim()) {
      setFinanceGoalsData(prev => ({
        ...prev,
        customGoals: [...prev.customGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({ name: '', target: '' });
      setShowNewGoalCard(false);
    }
  };

  const removeFinanceCustomGoal = (id) => {
    setFinanceGoalsData(prev => ({
      ...prev,
      customGoals: prev.customGoals.filter(goal => goal.id !== id)
    }));
  };

  const saveFinanceGoals = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'financeData.goalsCompleted': true,
        'financeData.goalsData': financeGoalsData,
        'businessAssessmentProgress.financeGoalsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setFinanceGoalsCompleted(true);
      setShowFinanceGoalsPopup(false);
    } catch (error) {
      console.error('Error saving finance goals:', error);
      alert('Error saving finance goals. Please try again.');
    }
  };

  // Tech goals popup handlers
  const handleTechGoalsDataChange = (e) => {
    const { name, value } = e.target;
    setTechGoalsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addTechCustomGoal = () => {
    if (newGoal.name && newGoal.name.trim() && newGoal.target && newGoal.target.trim()) {
      setTechGoalsData(prev => ({
        ...prev,
        customGoals: [...prev.customGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({ name: '', target: '' });
      setShowNewGoalCard(false);
    }
  };

  const removeTechCustomGoal = (id) => {
    setTechGoalsData(prev => ({
      ...prev,
      customGoals: prev.customGoals.filter(goal => goal.id !== id)
    }));
  };

  const saveTechGoals = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'techData.goalsCompleted': true,
        'techData.goalsData': techGoalsData,
        'businessAssessmentProgress.techGoalsCompleted': true,
        lastUpdated: new Date().toISOString()
      });
      
      setTechGoalsCompleted(true);
      setShowTechGoalsPopup(false);
    } catch (error) {
      console.error('Error saving tech goals:', error);
      alert('Error saving tech goals. Please try again.');
    }
  };

  // Function to save progress updates
  const saveProgressUpdate = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'businessAssessmentProgress.progress': progress,
        'businessAssessmentProgress.currentSection': currentSection,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving progress update:', error);
    }
  };



  // Helper function to remove undefined values from an object (Firestore doesn't allow undefined)
  const removeUndefinedValues = (obj) => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item));
    }
    if (typeof obj === 'object') {
      const cleaned = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          cleaned[key] = removeUndefinedValues(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  };

  const handleSubmit = async () => {
    // Validate Technology section (required when it's the last section)
    if (currentSection === getCurrentSections().length - 1) {
      if (formData.techRating === 0 || !formData.whatCRM || String(formData.whatCRM).trim() === '' ||
          formData.crmOptimization === undefined || formData.crmOptimization === null || formData.crmOptimization === 0) {
        return; // Block submit - required tech fields missing
      }
    }
    // The bar sits at 99% while they're on the last step; finishing is what
    // takes it to 100.
    setProgress(100);
    try {
      let portalOnboardingContinueToNda = false;
      // Save all the data to the user's document
      if (currentUser) {
        // Determine projectType based on contractorType
        let projectTypeValue = '';
        if (formData.contractorType === 'Subcontractor') {
          // For subcontractors, projectType is the selected subcontractor focus area
          // Use the first selected focus area if multiple are selected
          if (Array.isArray(formData.subcontractorFocus) && formData.subcontractorFocus.length > 0) {
            projectTypeValue = formData.subcontractorFocus[0];
          } else if (formData.subcontractorFocus) {
            projectTypeValue = formData.subcontractorFocus;
          }
        } else {
          // For General Contractors, projectType is residential/commercial/both
          projectTypeValue = formData.projectType || '';
        }

        // Convert projectFocusType array to single string value (only for General Contractors)
        let projectFocusTypeValue = '';
        if (formData.contractorType === 'General Contractor') {
          if (Array.isArray(formData.projectFocusType)) {
            const hasNewConstruction = formData.projectFocusType.includes('newConstruction');
            const hasRemodels = formData.projectFocusType.includes('remodels');
            if (hasNewConstruction && hasRemodels) {
              projectFocusTypeValue = 'Both';
            } else if (hasNewConstruction) {
              projectFocusTypeValue = 'newConstruction';
            } else if (hasRemodels) {
              projectFocusTypeValue = 'Remodels';
            }
          } else if (formData.projectFocusType) {
            projectFocusTypeValue = formData.projectFocusType;
          }
        }

        const userData = {
          // Business Assessment - Core business info + all assessment fields
          businessAssessment: {
            contractorType: formData.contractorType,
            projectType: projectTypeValue,
            ...(formData.contractorType === 'General Contractor' && projectFocusTypeValue ? { projectFocusType: projectFocusTypeValue } : {}),
            // Sales fields
            salesRating: formData.salesRating,
            closeRatio: formData.closeRatio,
            referralBusinessPercentage: formData.referralBusinessPercentage,
            // Operations fields
            operationsRating: formData.operationsRating,
            cultureGrade: formData.cultureGrade,
            // Finance fields
            financeRating: formData.financeRating,
            knowsOverhead: formData.knowsOverhead,
            knowsLaborBurden: formData.knowsLaborBurden,
            knowsProjectRunway: formData.knowsProjectRunway,
            // Marketing fields
            marketingRating: formData.marketingRating,
            marketingAgencyType: formData.marketingAgencyType,
            engagesSocialMedia: formData.engagesSocialMedia,
            engagesPaidAds: formData.engagesPaidAds,
            engagesNewsletter: formData.engagesNewsletter,
            // Tech fields
            techRating: formData.techRating
          },
          
          // Sales Data (simplified)
          salesData: {
            salesRating: formData.salesRating,
            closeRatio: formData.closeRatio,
            referralBusinessPercentage: formData.referralBusinessPercentage,
            repeatCustomerPercentage: formData.repeatCustomerPercentage,
            salesmanCount: formData.salesmanCount,
            revenueData: {
              oneYearAgo: revenueData.oneYearAgo,
              currentYearProjection: revenueData.currentYearProjection,
              nextYearGoal: revenueData.nextYearGoal
            }
          },
          
          // Operations Data (simplified)
          operationsData: {
            operationsRating: formData.operationsRating,
            hasSOPs: formData.hasSOPs,
            hasJobDescriptions: formData.hasJobDescriptions,
            hasKPIs: formData.hasKPIs,
            companyCultureRating: formData.companyCultureRating,
            teamSize: formData.teamSize,
            teamGrade: formData.teamGrade,
            cultureGrade: formData.cultureGrade
          },
          
          // Finance Data (simplified)
          financeData: {
            financeRating: formData.financeRating,
            hasMonthlyReconciliation: formData.hasMonthlyReconciliation,
            hasCashFlowProjection: formData.hasCashFlowProjection,
            hasProjectBudgetsVsActuals: formData.hasProjectBudgetsVsActuals,
            hasDebt: formData.hasDebt,
            hasFinancialPartners: formData.hasFinancialPartners,
            hasCreditLine: formData.hasCreditLine,
            knowsOverhead: formData.knowsOverhead,
            knowsLaborBurden: formData.knowsLaborBurden,
            knowsProjectRunway: formData.knowsProjectRunway,
            unencumberedCash: formData.unencumberedCash,
            grossProfitMargin: formData.grossProfitMargin,
            netProfitMargin: formData.netProfitMargin,
            incomeStatementUrl: formData.incomeStatementUrl,
            incomeStatementFileName: formData.incomeStatementFileName,
            balanceSheetUrl: formData.balanceSheetUrl,
            balanceSheetFileName: formData.balanceSheetFileName,
            cashFlowStatementUrl: formData.cashFlowStatementUrl,
            cashFlowStatementFileName: formData.cashFlowStatementFileName
          },
          
          // Marketing Data (simplified)
          marketingData: {
            marketingRating: formData.marketingRating,
            monthlyMarketingBudget: formData.monthlyMarketingBudget,
            workingWithMarketingAgency: formData.workingWithMarketingAgency,
            marketingAgencyDescription: formData.marketingAgencyDescription,
            runningDigitalAds: formData.runningDigitalAds,
            digitalAdPlatforms: formData.digitalAdPlatforms,
            websiteManager: formData.websiteManager,
            marketingAgencyType: formData.marketingAgencyType,
            engagesSocialMedia: formData.engagesSocialMedia,
            engagesPaidAds: formData.engagesPaidAds,
            engagesNewsletter: formData.engagesNewsletter
          },
          
          // Tech Data (simplified)
          techData: {
            techRating: formData.techRating,
            crmOptimization: formData.crmOptimization,
            whatCRM: formData.whatCRM,
            hasAutomations: formData.hasAutomations,
            hasAITools: formData.hasAITools,
            hasWebsitePlugins: formData.hasWebsitePlugins
          },
          
          // Progress tracking
          businessAssessmentProgress: {
            progress: 100,
            revenueCompleted: revenueCompleted,
            goalsCompleted: goalsCompleted,
            gpmCompleted: gpmCompleted,
            operationsGoalsCompleted: operationsGoalsCompleted,
            marketingGoalsCompleted: marketingGoalsCompleted,
            financeGoalsCompleted: financeGoalsCompleted,
            techGoalsCompleted: techGoalsCompleted,
            orgChartCompleted: orgChartCompleted,
            npmCompleted: npmCompleted,
            leadsCompleted: leadsCompleted,
            techStackCompleted: techStackCompleted,
            mvvCompleted: mvvCompleted,
            currentSection: currentSection
          },
          
          // Timestamp
          lastUpdated: new Date().toISOString()
        };

        // Check if we're in start flow mode (no onboarding props)
        const isStartFlow = !updateOnboardingStep || !goToNextStep;
        
        if (isStartFlow) {
          // Get existing user data to preserve completed steps
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          let existingCompletedSteps = [];
          let existingUserData = {};
          
          if (userDoc.exists()) {
            existingUserData = userDoc.data();
            if (existingUserData.startFlowProgress && existingUserData.startFlowProgress.completedSteps) {
              existingCompletedSteps = existingUserData.startFlowProgress.completedSteps;
            }
          }

          const os = existingUserData.onboardingStatus;
          if (
            existingUserData.userType === 'portal' &&
            os &&
            !os.completed &&
            os.currentStep === 'existing-conditions' &&
            os.steps &&
            os.steps['existing-conditions']
          ) {
            userData.onboardingStatus = {
              ...os,
              currentStep: 'nda',
              steps: {
                ...os.steps,
                'existing-conditions': {
                  ...os.steps['existing-conditions'],
                  completed: true,
                },
              },
            };
            portalOnboardingContinueToNda = true;
          }
          
          // Step 3 (Solutions) should only be marked when the user actually completes the Solutions page
          // Remove step 3 if it exists, as ExistingConditions should never add it
          if (existingCompletedSteps.includes(3)) {
            existingCompletedSteps = existingCompletedSteps.filter(step => step !== 3);
          }
          
          // Add step 2 if not already completed
          if (!existingCompletedSteps.includes(2)) {
            existingCompletedSteps.push(2);
          }
          
          // Update start flow progress to mark step 2 (Assessment) as completed
          userData.startFlowProgress = {
            completedSteps: existingCompletedSteps,
            lastUpdated: new Date().toISOString()
          };
        }

        // Remove undefined values before saving (Firestore doesn't allow undefined)
        const cleanedUserData = removeUndefinedValues(userData);

        await updateDoc(doc(db, 'users', currentUser.uid), cleanedUserData);
        
        // Copy all assessment data to lead document if user has a lead account
        try {
          console.log('=== COPYING ALL ASSESSMENT DATA TO LEAD DOCUMENT ===');
          console.log('Business Assessment Data:', cleanedUserData.businessAssessment);
          console.log('Sales Data:', cleanedUserData.salesData);
          console.log('Operations Data:', cleanedUserData.operationsData);
          console.log('Finance Data:', cleanedUserData.financeData);
          console.log('Marketing Data:', cleanedUserData.marketingData);
          console.log('Tech Data:', cleanedUserData.techData);
          console.log('Business Assessment Progress:', cleanedUserData.businessAssessmentProgress);
          
          const success = await copyAllAssessmentDataToLead(
            currentUser.email,
            cleanedUserData
          );
          
          if (success) {
            console.log('Successfully copied all assessment data to lead document');
          } else {
            console.log('No lead account found or failed to copy assessment data');
          }
        } catch (leadCopyError) {
          console.error('Error copying assessment data to lead:', leadCopyError);
          // Continue even if lead copy fails
        }
      }

      // Handle navigation based on flow type
      const isStartFlow = !updateOnboardingStep || !goToNextStep;
      
      if (isStartFlow) {
        if (portalOnboardingContinueToNda) {
          navigate('/start/nda');
        } else {
          navigate('/start/dashboard');
        }
      } else {
        // Mark this step as completed and move to next onboarding step
        updateOnboardingStep(currentStep, true);
        goToNextStep();
      }
    } catch (error) {
      console.error('Error saving existing conditions:', error);
      
      const isStartFlow = !updateOnboardingStep || !goToNextStep;
      
      if (isStartFlow) {
        // In start flow, navigate back even if save fails
        navigate('/start/dashboard');
      } else {
        // In dev mode, just continue to next step even if save fails
        if (process.env.NODE_ENV === 'development') {
          updateOnboardingStep(currentStep, true);
          goToNextStep();
        }
      }
    }
  };

  const nextSection = () => {
    // Validate Project Type section for General Contractors
    if (currentSection === 1 && formData.contractorType === 'General Contractor') {
      // Check project type first (required)
      if (!formData.projectType) {
        setShowProjectTypeError(true);
        setShowProjectFocusError(false); // Clear focus error if type is missing
        return; // Button should be disabled, but just in case
      }
      // Check project focus (required)
      if (!formData.projectFocusType || formData.projectFocusType.length === 0) {
        setShowProjectFocusError(true);
        setShowProjectTypeError(false); // Clear type error if focus is missing
        return;
      }
      // Save project type data before navigating to ensure Project Focus is saved
      saveProjectTypeData();
    }
    
    // Validate Sales section
    const isSalesSection = 
      (currentSection === 2 && (formData.contractorType === 'General Contractor' || formData.contractorType === 'Subcontractor')) ||
      (currentSection === 3 && formData.contractorType === 'Home Improvement');
    
    if (isSalesSection) {
      // Check sales rating (required)
      if (formData.salesRating === 0) {
        setShowSalesRatingError(true);
        return;
      }
      
      // Check revenue fields (all required) - button will be disabled, no error message needed
      const oneYearAgo = String(revenueData.oneYearAgo || '').trim();
      const currentYearProjection = String(revenueData.currentYearProjection || '').trim();
      const nextYearGoal = String(revenueData.nextYearGoal || '').trim();
      
      if (!oneYearAgo || !currentYearProjection || !nextYearGoal) {
        return; // Button will be disabled, no need to show error
      }
      
      // Save revenue data before navigating to ensure nextYearGoal is saved
      saveRevenue();
    }
    
    // Validate Operations section
    const isOperationsSection = 
      (currentSection === 3 && (formData.contractorType === 'General Contractor' || formData.contractorType === 'Subcontractor')) ||
      (currentSection === 4 && formData.contractorType === 'Home Improvement');
    
    if (isOperationsSection) {
      // Check operations rating (required)
      if (formData.operationsRating === 0) {
        setShowOperationsRatingError(true);
        return;
      }
      
      // Check team size (required)
      const teamSize = String(formData.teamSize || '').trim();
      if (!teamSize) {
        setShowOperationsRatingError(false);
        return; // Button will be disabled, no need to show error
      }
      
      // Check team grade (required)
      if (!formData.teamGrade) {
        setShowOperationsRatingError(false);
        return; // Button will be disabled, no need to show error
      }
      
      // Check culture grade (required)
      if (!formData.cultureGrade) {
        setShowOperationsRatingError(false);
        return; // Button will be disabled, no need to show error
      }
    }
    
    // Clear errors if validation passes
    setShowProjectTypeError(false);
    setShowProjectFocusError(false);
    setShowSalesRatingError(false);
    setShowOperationsRatingError(false);
    
    const currentSections = getCurrentSections();
    if (currentSection < currentSections.length - 1) {
      setCurrentSection(currentSection + 1);
      // Save progress update after state change
      setTimeout(() => saveProgressUpdate(), 100);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      // Save progress update after state change
      setTimeout(() => saveProgressUpdate(), 100);
    }
  };

  const renderContractorType = () => (
    <div className="grid grid-cols-2 gap-4 sm:gap-6">
      {CONTRACTOR_TYPE_OPTIONS.map((option) => (
        <OptionCard
          key={option.value}
          size="xl"
          emoji={option.emoji}
          label={option.value}
          description={option.description}
          selected={formData.contractorType === option.value}
          onClick={() => {
            setFormData(prev => ({ ...prev, contractorType: option.value }));
            saveContractorType(option.value);
          }}
        />
      ))}
    </div>
  );


  // Project Type Section
  // Three exclusive cards (Remodels / New Construction / Both) over the same
  // projectFocusType array the rest of the app already reads.
  const selectedProjectFocus = () => {
    const arr = formData.projectFocusType || [];
    const hasNew = arr.includes('newConstruction');
    const hasRemodels = arr.includes('remodels');
    if (hasNew && hasRemodels) return 'both';
    if (hasNew) return 'newConstruction';
    if (hasRemodels) return 'remodels';
    return '';
  };

  const setProjectFocus = (choice) => {
    const next = choice === 'both' ? ['remodels', 'newConstruction'] : [choice];
    setFormData(prev => ({ ...prev, projectFocusType: next }));
    setShowProjectFocusError(false);
    setTimeout(() => saveProjectTypeData(), 100);
  };

  // Project Type Section
  const renderProjectType = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PROJECT_TYPE_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            size="lg"
            emoji={option.emoji}
            label={option.label}
            selected={formData.projectType === option.value}
            onClick={() => {
              setFormData(prev => ({ ...prev, projectType: option.value }));
              setShowProjectTypeError(false);
              setShowProjectFocusError(false);
              setTimeout(() => saveProjectTypeData(), 100);
            }}
          />
        ))}
      </div>

      {showProjectTypeError && (
        <AssessmentAlert>
          Please select a project type (Residential Only, Commercial Only, or Both) to continue.
        </AssessmentAlert>
      )}

      {formData.projectType && (
        <AssessmentPanel emoji="🎯" title="Project Focus">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PROJECT_FOCUS_OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                size="lg"
                emoji={option.emoji}
                label={option.label}
                selected={selectedProjectFocus() === option.value}
                onClick={() => setProjectFocus(option.value)}
              />
            ))}
          </div>

          {showProjectFocusError && (
            <AssessmentAlert>Please select at least one project focus to continue.</AssessmentAlert>
          )}
        </AssessmentPanel>
      )}
    </div>
  );





  const toggleSubcontractorFocus = (value) => {
    setFormData(prev => {
      const arr = prev.subcontractorFocus;
      if (arr.includes(value)) {
        // Remove
        return { ...prev, subcontractorFocus: arr.filter(v => v !== value) };
      } else {
        // Add
        return { ...prev, subcontractorFocus: [...arr, value] };
      }
    });
    // Save data after state update
    setTimeout(() => saveSubcontractorFocusData(), 100);
  };


  const toggleHomeImprovementFocus = (value) => {
    setFormData(prev => {
      const arr = prev.homeImprovementFocus;
      if (arr.includes(value)) {
        // Remove
        return { ...prev, homeImprovementFocus: arr.filter(v => v !== value) };
      } else {
        // Add
        return { ...prev, homeImprovementFocus: [...arr, value] };
      }
    });
    // Save data after state update
    setTimeout(() => saveHomeImprovementFocusData(), 100);
  };

  const setPreferredHomeImprovementFocus = (value) => {
    setFormData(prev => ({ ...prev, preferredHomeImprovementFocus: value }));
    // Save data after state update
    setTimeout(() => saveHomeImprovementFocusData(), 100);
  };

  const setMostCommonHomeImprovementFocus = (value) => {
    setFormData(prev => ({ ...prev, mostCommonHomeImprovementFocus: value }));
    // Save data after state update
    setTimeout(() => saveHomeImprovementFocusData(), 100);
  };

  // Marketing helper functions
  const toggleDigitalAdPlatform = (platform) => {
    setFormData(prev => {
      const arr = prev.digitalAdPlatforms;
      if (arr.includes(platform)) {
        return { ...prev, digitalAdPlatforms: arr.filter(p => p !== platform) };
      } else {
        return { ...prev, digitalAdPlatforms: [...arr, platform] };
      }
    });
    setTimeout(() => saveMarketingAssessmentData(), 100);
  };

  const toggleMarketingPlatform = (platform) => {
    setFormData(prev => {
      const arr = prev.marketingPlatforms;
      if (arr.includes(platform)) {
        return { ...prev, marketingPlatforms: arr.filter(p => p !== platform) };
      } else {
        return { ...prev, marketingPlatforms: [...arr, platform] };
      }
    });
    setTimeout(() => saveMarketingAssessmentData(), 100);
  };

  // Technology helper functions
  const toggleChatGPTFeature = (feature) => {
    setFormData(prev => {
      const arr = prev.chatgptFeatures;
      if (arr.includes(feature)) {
        return { ...prev, chatgptFeatures: arr.filter(f => f !== feature) };
      } else {
        return { ...prev, chatgptFeatures: [...arr, feature] };
      }
    });
    setTimeout(() => saveTechAssessmentData(), 100);
  };

  // Client Types Section for Subcontractors
  // Client Types Section for Subcontractors
  const renderClientTypes = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {CLIENT_TYPE_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            size="lg"
            emoji={option.emoji}
            label={option.label}
            description={option.description}
            selected={formData.clientTypes === option.value}
            onClick={() => {
              setFormData(prev => ({ ...prev, clientTypes: option.value }));
              setTimeout(() => saveClientTypesData(), 100);
            }}
          />
        ))}
      </div>

      {(formData.clientTypes === 'gcs' || formData.clientTypes === 'both') && (
        <AssessmentPanel emoji="🤝" title="General Contractor Relationship">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GC_RELATIONSHIP_OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                size="lg"
                emoji={option.emoji}
                label={option.label}
                description={option.description}
                selected={formData.gcRelationship === option.value}
                onClick={() => {
                  setFormData(prev => ({ ...prev, gcRelationship: option.value }));
                  setTimeout(() => saveClientTypesData(), 100);
                }}
              />
            ))}
          </div>
        </AssessmentPanel>
      )}

      {formData.clientTypes === 'both' && (
        <AssessmentPanel
          emoji="⚖️"
          title="What percentage of your work is Homeowners vs. General Contractors?"
        >
          <div className="mx-auto max-w-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div className="text-center">
                <div className="text-3xl leading-none" aria-hidden="true">🏡</div>
                <div className="mt-1 text-2xl font-extrabold text-red-600 dark:text-red-400">
                  {formData.homeownersPercentage}%
                </div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">Homeowners</div>
              </div>
              <div className="text-center">
                <div className="text-3xl leading-none" aria-hidden="true">👷</div>
                <div className="mt-1 text-2xl font-extrabold text-gray-700 dark:text-gray-200">
                  {100 - formData.homeownersPercentage}%
                </div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  General Contractors
                </div>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.homeownersPercentage}
              onChange={e => {
                setFormData(prev => ({ ...prev, homeownersPercentage: Number(e.target.value) }));
                setTimeout(() => saveClientTypesData(), 100);
              }}
              className="w-full cursor-pointer appearance-none"
            />
          </div>
        </AssessmentPanel>
      )}
    </div>
  );

  // Subcontractor Focus Section
  // Subcontractor Focus Section
  const renderSubcontractorFocus = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {subcontractorFocusOptions.map(option => (
          <OptionCard
            key={option}
            size="sm"
            emoji={SUBCONTRACTOR_FOCUS_EMOJI[option] || '🧰'}
            label={option}
            selected={formData.subcontractorFocus.includes(option)}
            onClick={() => toggleSubcontractorFocus(option)}
          />
        ))}
      </div>
    </div>
  );

  // Home Improvement Focus Section
  // Home Improvement Focus Section
  const renderHomeImprovementFocus = () => (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-xl border border-gray-200 bg-white/70 px-4 py-3 text-xs dark:border-gray-700 dark:bg-gray-800/70">
        <span className="font-semibold text-gray-700 dark:text-gray-200">
          Pick your services, then flag:
        </span>
        <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
          <span aria-hidden="true">⭐</span> Preferred focus
        </span>
        <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
          <span aria-hidden="true">📈</span> Most common focus
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {homeImprovementFocusOptions.map(option => {
          const chosen = formData.homeImprovementFocus.includes(option);
          const isPreferred = formData.preferredHomeImprovementFocus === option;
          const isMostCommon = formData.mostCommonHomeImprovementFocus === option;
          return (
            <div key={option} className="relative h-full">
              <OptionCard
                size="sm"
                emoji={HOME_IMPROVEMENT_FOCUS_EMOJI[option] || '🧰'}
                label={option}
                selected={chosen}
                onClick={() => toggleHomeImprovementFocus(option)}
                className="pb-12"
              />
              <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-2">
                <button
                  type="button"
                  title="Mark as Preferred Focus"
                  onClick={e => { e.stopPropagation(); setPreferredHomeImprovementFocus(option); }}
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-sm transition-all duration-200 ${
                    isPreferred
                      ? 'bg-yellow-400 shadow-md'
                      : 'bg-gray-100 grayscale dark:bg-gray-700'
                  } ${chosen ? 'opacity-100 hover:scale-110' : 'pointer-events-none opacity-25'}`}
                >
                  <span aria-hidden="true">⭐</span>
                </button>
                <button
                  type="button"
                  title="Mark as Most Common Focus"
                  onClick={e => { e.stopPropagation(); setMostCommonHomeImprovementFocus(option); }}
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-sm transition-all duration-200 ${
                    isMostCommon
                      ? 'bg-blue-400 shadow-md'
                      : 'bg-gray-100 grayscale dark:bg-gray-700'
                  } ${chosen ? 'opacity-100 hover:scale-110' : 'pointer-events-none opacity-25'}`}
                >
                  <span aria-hidden="true">📈</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Sales Rating Section
  const renderSalesRating = () => {
    // Check if we're in onboarding mode - use a broader check
    // Check if onboardingStatus exists and has currentStep, or if currentStep prop exists (from OnboardingWrapper)
    // Also check if we're on the onboarding route path (portal OR start) or if onboarding-specific props exist
    const isOnboardingRoute = location.pathname.includes('/start/existing-conditions');
    const hasOnboardingProps = currentStep || updateOnboardingStep || goToNextStep;
    const isOnboarding = (onboardingStatus && onboardingStatus.currentStep) || hasOnboardingProps || isOnboardingRoute || process.env.NODE_ENV === 'development';

    if (isOnboarding) {
      return (
        <div className="space-y-6">
          <AssessmentPanel
            title="How would you rate your current sales performance?"
            description="Click on the stars to rate yourself from 1-5"
          >
            <RatingScale
              value={formData.salesRating}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, salesRating: value }));
                setShowSalesRatingError(false);
                setTimeout(() => saveSalesAssessmentData(), 100);
              }}
            />
            {showSalesRatingError && (
              <AssessmentAlert>Please rate your current sales performance to continue.</AssessmentAlert>
            )}
          </AssessmentPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AssessmentPanel
              emoji="🎯"
              title="Close Ratio"
              description="What percentage of qualified leads convert to sales?"
            >
              <PercentDial
                darkMode={darkMode}
                value={formData.closeRatio || 0}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, closeRatio: value }));
                  setTimeout(() => saveSalesAssessmentData(), 100);
                }}
              />
            </AssessmentPanel>

            <AssessmentPanel
              emoji="🗣️"
              title="Referral based business %"
              description="What percentage of your business comes from referrals?"
            >
              <PercentDial
                darkMode={darkMode}
                value={formData.referralBusinessPercentage || 0}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, referralBusinessPercentage: value }));
                  setTimeout(() => saveSalesAssessmentData(), 100);
                }}
              />
            </AssessmentPanel>
          </div>

          <AssessmentPanel
            emoji="💵"
            title="Revenue"
            description="Where you were, where you are, and where you're headed."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <UnitAmountField
                emoji="📅"
                label={`${new Date().getFullYear() - 1} Revenue`}
                name="oneYearAgo"
                value={revenueData.oneYearAgo}
                onChange={(e) => {
                  handleRevenueDataChange(e);
                  setTimeout(() => saveRevenue(), 100);
                }}
                placeholder="1.5"
              />
              <UnitAmountField
                emoji="📊"
                label={`${new Date().getFullYear()} Revenue Projection`}
                name="currentYearProjection"
                value={revenueData.currentYearProjection}
                onChange={(e) => {
                  handleRevenueDataChange(e);
                  setTimeout(() => saveRevenue(), 100);
                }}
                placeholder="2.0"
              />
              <UnitAmountField
                emoji="🚀"
                label={`${new Date().getFullYear() + 1} Revenue Goal`}
                name="nextYearGoal"
                value={revenueData.nextYearGoal}
                onChange={(e) => {
                  handleRevenueDataChange(e);
                  setTimeout(() => saveRevenue(), 100);
                }}
                placeholder="3.0"
              />
            </div>
          </AssessmentPanel>

          {onboardingProgress?.salesAssessment && (
            <CompletedNote>Sales Assessment Completed!</CompletedNote>
          )}
        </div>
      );
    }
    
    // Default star rating for non-onboarding mode
    return (
      <div>
        <StarRating
          rating={formData.salesRating}
          onRatingChange={(rating) => setFormData(prev => ({ ...prev, salesRating: rating }))}
          title="Rate Your Sales Performance"
        />
      </div>
    );
  };

  // Operations Rating Section
  const renderOperationsRating = () => {
    // Check if we're in onboarding mode - use a broader check
    // Check if onboardingStatus exists and has currentStep, or if currentStep prop exists (from OnboardingWrapper)
    // Also check if we're on the onboarding route path (portal OR start) or if onboarding-specific props exist
    const isOnboardingRoute = location.pathname.includes('/start/existing-conditions');
    const hasOnboardingProps = currentStep || updateOnboardingStep || goToNextStep;
    const isOnboarding = (onboardingStatus && onboardingStatus.currentStep) || hasOnboardingProps || isOnboardingRoute || process.env.NODE_ENV === 'development';
    
    if (isOnboarding) {
      return (
        <div className="space-y-6">
          <AssessmentPanel
            title="How would you rate your current operations performance?"
            description="Click on the stars to rate yourself from 1-5"
          >
            <RatingScale
              value={formData.operationsRating}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, operationsRating: value }));
                setShowOperationsRatingError(false);
                setTimeout(() => saveOperationsAssessmentData(), 100);
              }}
            />
            {showOperationsRatingError && (
              <AssessmentAlert>
                Please rate your current operations performance to continue.
              </AssessmentAlert>
            )}
          </AssessmentPanel>

          <AssessmentPanel emoji="📋" title="Do you have the following?">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <HaveItCard
                emoji="📚"
                label="SOP Library"
                info="Do you have a library of written Standard Operating Procedures (SOPs) for your business?"
                selected={formData.hasSOPs === 'yes'}
                onClick={() => {
                  setFormData(prev => ({ ...prev, hasSOPs: prev.hasSOPs === 'yes' ? 'no' : 'yes' }));
                  setTimeout(() => saveOperationsAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="🗂️"
                label="Job Descriptions"
                info="Do you have a library of written job descriptions for each role in your business?"
                selected={formData.hasJobDescriptions === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    hasJobDescriptions: prev.hasJobDescriptions === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveOperationsAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="📊"
                label="KPIs"
                info="Do you have and utilize Key Performance Indicators (KPIs) for your business and each employee?"
                selected={formData.hasKPIs === 'yes'}
                onClick={() => {
                  setFormData(prev => ({ ...prev, hasKPIs: prev.hasKPIs === 'yes' ? 'no' : 'yes' }));
                  setTimeout(() => saveOperationsAssessmentData(), 100);
                }}
              />
            </div>
          </AssessmentPanel>

          <AssessmentPanel emoji="👥" title="Team Size">
            <div className="mx-auto max-w-xs">
              <input
                type="number"
                name="teamSize"
                value={formData.teamSize || ''}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, teamSize: e.target.value }));
                  setTimeout(() => saveOperationsAssessmentData(), 100);
                }}
                min="0"
                className="block w-full rounded-xl border-2 border-gray-200 bg-white py-3 px-4 text-center text-lg font-bold text-gray-900 shadow-sm transition-all duration-200 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-red-800"
                placeholder="0"
              />
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border-2 border-gray-200 bg-white p-5 text-center dark:border-gray-700 dark:bg-gray-800/70">
                <h5 className="mb-4 flex items-center justify-center gap-2 text-base font-bold text-gray-900 dark:text-white">
                  <span aria-hidden="true">🏅</span>
                  Team Grade
                </h5>
                <GradePicker
                  value={formData.teamGrade}
                  onChange={(grade) => {
                    setFormData(prev => ({ ...prev, teamGrade: grade }));
                    setTimeout(() => saveOperationsAssessmentData(), 100);
                  }}
                />
              </div>

              <div className="rounded-2xl border-2 border-gray-200 bg-white p-5 text-center dark:border-gray-700 dark:bg-gray-800/70">
                <h5 className="mb-4 flex items-center justify-center gap-2 text-base font-bold text-gray-900 dark:text-white">
                  <span aria-hidden="true">❤️</span>
                  Culture Grade
                </h5>
                <GradePicker
                  value={formData.cultureGrade}
                  onChange={(grade) => {
                    setFormData(prev => ({ ...prev, cultureGrade: grade }));
                    setTimeout(() => saveOperationsAssessmentData(), 100);
                  }}
                />
              </div>
            </div>
          </AssessmentPanel>

          {onboardingProgress?.operationsAssessment && (
            <CompletedNote>Operations Assessment Completed!</CompletedNote>
          )}
        </div>
      );
    }
    
    // Default star rating for non-onboarding mode
    return (
      <div>
        <StarRating
          rating={formData.operationsRating}
          onRatingChange={(rating) => {
            setFormData(prev => ({ ...prev, operationsRating: rating }));
            setTimeout(() => saveOperationsAssessmentData({ operationsRating: rating }), 100);
          }}
          title="Rate Your Operations"
        />
      </div>
    );
  };

  // Finance Rating Section
  const renderFinanceRating = () => {
    // Check if we're in onboarding mode - use a broader check
    // Check if onboardingStatus exists and has currentStep, or if currentStep prop exists (from OnboardingWrapper)
    // Also check if we're on the onboarding route path (portal OR start) or if onboarding-specific props exist
    const isOnboardingRoute = location.pathname.includes('/start/existing-conditions');
    const hasOnboardingProps = currentStep || updateOnboardingStep || goToNextStep;
    const isOnboarding = (onboardingStatus && onboardingStatus.currentStep) || hasOnboardingProps || isOnboardingRoute || process.env.NODE_ENV === 'development';
    
    // One uploader for all three statements — same Storage path shape and the
    // same "stash URL + file name on the form, then save" flow as before.
    const uploadStatement = async (event, { folder, urlField, nameField, setBusy }) => {
      const file = event.target.files[0];
      if (!file) return;
      try {
        setBusy(true);
        const storageRef = ref(storage, `finance/${currentUser.uid}/${folder}_${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        setFormData(prev => ({ ...prev, [urlField]: downloadURL, [nameField]: file.name }));
        setTimeout(() => saveFinanceAssessmentData(), 100);
      } catch (error) {
        console.error(`Error uploading ${folder}:`, error);
      } finally {
        setBusy(false);
      }
    };

    if (isOnboarding) {
      return (
        <div className="space-y-6">
          <AssessmentPanel
            title="How would you rate your current financial management?"
            description="Click on the stars to rate yourself from 1-5"
          >
            <RatingScale
              value={formData.financeRating}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, financeRating: value }));
                setTimeout(() => saveFinanceAssessmentData(), 100);
              }}
            />
          </AssessmentPanel>

          <AssessmentPanel emoji="🧮" title="Do you know the following?">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <HaveItCard
                emoji="🧾"
                label="Overhead"
                info="Do you know your monthly overhead costs?"
                selected={formData.knowsOverhead === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    knowsOverhead: prev.knowsOverhead === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveFinanceAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="👷"
                label="Labor Burden"
                info="Do you know your labor burden rate?"
                selected={formData.knowsLaborBurden === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    knowsLaborBurden: prev.knowsLaborBurden === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveFinanceAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="🛫"
                label="Project Runway"
                info="Do you know your project runway (how long you can operate with current cash)?"
                selected={formData.knowsProjectRunway === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    knowsProjectRunway: prev.knowsProjectRunway === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveFinanceAssessmentData(), 100);
                }}
              />
            </div>
          </AssessmentPanel>

          <AssessmentPanel
            emoji="📈"
            title="Your numbers"
            description="Attach a statement for anything you'd like us to verify — optional, but it speeds up your roadmap."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <UnitAmountField
                emoji="💰"
                label="Unencumbered Cash"
                name="unencumberedCash"
                defaultUnit="K"
                value={formData.unencumberedCash || ''}
                onChange={(e) => {
                  handleChange(e);
                  setTimeout(() => saveFinanceAssessmentData(), 100);
                }}
                placeholder="250"
              >
                <UploadCard
                  id="balanceSheetUpload"
                  emoji="📄"
                  label="Balance Sheet"
                  accept=".pdf,.xlsx,.xls"
                  uploading={uploadingBalanceSheet}
                  uploaded={!!formData.balanceSheetUrl}
                  fileName={formData.balanceSheetFileName}
                  onChange={(e) => uploadStatement(e, {
                    folder: 'balanceSheet',
                    urlField: 'balanceSheetUrl',
                    nameField: 'balanceSheetFileName',
                    setBusy: setUploadingBalanceSheet,
                  })}
                />
              </UnitAmountField>

              <FieldCard
                emoji="📈"
                label="Gross Profit Margin"
                name="grossProfitMargin"
                suffix="%"
                value={formData.grossProfitMargin || ''}
                onChange={(e) => {
                  handleChange(e);
                  setTimeout(() => saveFinanceAssessmentData({ grossProfitMargin: e.target.value }), 100);
                }}
                placeholder="Enter GPM %"
              >
                <UploadCard
                  id="incomeStatementUpload"
                  emoji="📊"
                  label="Profit Loss"
                  accept=".pdf,.xlsx,.xls"
                  uploading={uploadingIncomeStatement}
                  uploaded={!!formData.incomeStatementUrl}
                  fileName={formData.incomeStatementFileName}
                  onChange={(e) => uploadStatement(e, {
                    folder: 'incomeStatement',
                    urlField: 'incomeStatementUrl',
                    nameField: 'incomeStatementFileName',
                    setBusy: setUploadingIncomeStatement,
                  })}
                />
              </FieldCard>

              <FieldCard
                emoji="💎"
                label="Net Profit Margin"
                name="netProfitMargin"
                suffix="%"
                value={formData.netProfitMargin || ''}
                onChange={(e) => {
                  handleChange(e);
                  setTimeout(() => saveFinanceAssessmentData({ netProfitMargin: e.target.value }), 100);
                }}
                placeholder="Enter NPM %"
              >
                <UploadCard
                  id="cashFlowStatementUpload"
                  emoji="💧"
                  label="Cash Flow"
                  accept=".pdf,.xlsx,.xls"
                  uploading={uploadingCashFlowStatement}
                  uploaded={!!formData.cashFlowStatementUrl}
                  fileName={formData.cashFlowStatementFileName}
                  onChange={(e) => uploadStatement(e, {
                    folder: 'cashFlowStatement',
                    urlField: 'cashFlowStatementUrl',
                    nameField: 'cashFlowStatementFileName',
                    setBusy: setUploadingCashFlowStatement,
                  })}
                />
              </FieldCard>
            </div>
          </AssessmentPanel>

          {onboardingProgress?.financeAssessment && (
            <CompletedNote>Finance Assessment Completed!</CompletedNote>
          )}
        </div>
      );
    }
    
    // Default star rating for non-onboarding mode
    return (
      <StarRating
        rating={formData.financeRating}
        onRatingChange={(rating) => setFormData(prev => ({ ...prev, financeRating: rating }))}
        title="Rate Your Financial Management"
      />
    );
  };

  // Marketing Rating Section
  const renderMarketingRating = () => {
    // Check if we're in onboarding mode - use a broader check
    // Check if onboardingStatus exists and has currentStep, or if currentStep prop exists (from OnboardingWrapper)
    // Also check if we're on the onboarding route path (portal OR start) or if onboarding-specific props exist
    const isOnboardingRoute = location.pathname.includes('/start/existing-conditions');
    const hasOnboardingProps = currentStep || updateOnboardingStep || goToNextStep;
    const isOnboarding = (onboardingStatus && onboardingStatus.currentStep) || hasOnboardingProps || isOnboardingRoute || process.env.NODE_ENV === 'development';
    
    if (isOnboarding) {
      return (
        <div className="space-y-6">
          <AssessmentPanel
            title="How would you rate your current marketing performance?"
            description="Click on the stars to rate yourself from 1-5"
          >
            <RatingScale
              value={formData.marketingRating}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, marketingRating: value }));
                setTimeout(() => saveMarketingAssessmentData(), 100);
              }}
            />
          </AssessmentPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AssessmentPanel emoji="🤝" title="Are you currently working with a marketing agency?">
              <div className="grid grid-cols-2 gap-4">
                <OptionCard
                  emoji="👍"
                  label="Yes"
                  selected={formData.workingWithMarketingAgency === 'yes'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, workingWithMarketingAgency: 'yes' }));
                    setTimeout(() => saveMarketingAssessmentData(), 100);
                  }}
                />
                <OptionCard
                  emoji="👎"
                  label="No"
                  selected={formData.workingWithMarketingAgency === 'no'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, workingWithMarketingAgency: 'no' }));
                    setTimeout(() => saveMarketingAssessmentData(), 100);
                  }}
                />
              </div>
            </AssessmentPanel>

            <AssessmentPanel emoji="🌐" title="Who currently manages your website?">
              <div className="grid grid-cols-2 gap-4">
                <OptionCard
                  emoji="🏠"
                  label="In House"
                  selected={formData.marketingAgencyType === 'In House'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, marketingAgencyType: 'In House' }));
                    setTimeout(() => saveMarketingAssessmentData(), 100);
                  }}
                />
                <OptionCard
                  emoji="🏢"
                  label="External"
                  selected={formData.marketingAgencyType === 'External'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, marketingAgencyType: 'External' }));
                    setTimeout(() => saveMarketingAssessmentData(), 100);
                  }}
                />
              </div>
            </AssessmentPanel>
          </div>

          <AssessmentPanel emoji="📢" title="Do you engage in the following?">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <HaveItCard
                emoji="📱"
                label="Social Media"
                selected={formData.engagesSocialMedia === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    engagesSocialMedia: prev.engagesSocialMedia === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveMarketingAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="🎯"
                label="Paid Ads"
                selected={formData.engagesPaidAds === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    engagesPaidAds: prev.engagesPaidAds === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveMarketingAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="📧"
                label="Newsletter"
                selected={formData.engagesNewsletter === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    engagesNewsletter: prev.engagesNewsletter === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveMarketingAssessmentData(), 100);
                }}
              />
            </div>
          </AssessmentPanel>

          {onboardingProgress?.marketingAssessment && (
            <CompletedNote>Marketing Assessment Completed!</CompletedNote>
          )}
        </div>
      );
    }
    
    // Default star rating for non-onboarding mode
    return (
      <StarRating
        rating={formData.marketingRating}
        onRatingChange={(rating) => setFormData(prev => ({ ...prev, marketingRating: rating }))}
        title="Rate Your Marketing"
      />
    );
  };

  // Overall Rating Section - REMOVED

  // HR Rating Section - REMOVED

  // Technology Rating Section
  const renderTechRating = () => {
    // Check if we're in onboarding mode - use a broader check
    // Check if onboardingStatus exists and has currentStep, or if currentStep prop exists (from OnboardingWrapper)
    // Also check if we're on the onboarding route path (portal OR start) or if onboarding-specific props exist
    const isOnboardingRoute = location.pathname.includes('/start/existing-conditions');
    const hasOnboardingProps = currentStep || updateOnboardingStep || goToNextStep;
    const isOnboarding = (onboardingStatus && onboardingStatus.currentStep) || hasOnboardingProps || isOnboardingRoute || process.env.NODE_ENV === 'development';
    
    if (isOnboarding) {
      return (
        <div className="space-y-6">
          <AssessmentPanel
            title="How would you rate your current technology usage?"
            description="Click on the stars to rate yourself from 1-5"
          >
            <RatingScale
              value={formData.techRating}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, techRating: value }));
                setTimeout(() => saveTechAssessmentData(), 100);
              }}
            />
          </AssessmentPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AssessmentPanel emoji="🗂️" title="What CRM do you use?">
              <div className="grid grid-cols-2 gap-3">
                {['JobTread', 'Buildertrend', 'Procore', 'HubSpot'].map((crmName) => {
                  const imageKey = `crm-${crmName}`;
                  const hasImageError = crmImageErrors.has(imageKey);
                  const imageUrl = crmSoftwareImages[crmName];
                  const selected = formData.whatCRM === crmName;

                  return (
                    <button
                      key={crmName}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, whatCRM: crmName }));
                        setTimeout(() => saveTechAssessmentData(), 100);
                      }}
                      className={`group flex flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 transition-all duration-200 ${
                        selected
                          ? 'border-red-500 bg-red-50 shadow-lg shadow-red-500/10 -translate-y-0.5 dark:bg-red-900/25'
                          : 'border-gray-200 bg-white hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800/70 dark:hover:border-red-500/60'
                      }`}
                    >
                      <span className="flex h-11 w-11 items-center justify-center transition-transform duration-200 group-hover:scale-110">
                        {imageUrl && !hasImageError ? (
                          <img
                            src={imageUrl}
                            alt={crmName}
                            className="h-full w-full object-contain"
                            onError={() => setCrmImageErrors(prev => new Set([...prev, imageKey]))}
                          />
                        ) : (
                          <span className="text-3xl leading-none" aria-hidden="true">🗂️</span>
                        )}
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">{crmName}</span>
                    </button>
                  );
                })}

                <OptionCard
                  size="sm"
                  emoji="🚫"
                  label="None"
                  selected={formData.whatCRM === 'None'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, whatCRM: 'None' }));
                    setTimeout(() => saveTechAssessmentData(), 100);
                  }}
                />
                <OptionCard
                  size="sm"
                  emoji="✏️"
                  label="Other"
                  selected={formData.whatCRM === 'Other'}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, whatCRM: 'Other' }));
                    setTimeout(() => saveTechAssessmentData(), 100);
                  }}
                />
              </div>
            </AssessmentPanel>

            <AssessmentPanel
              emoji="⚙️"
              title="How optimized is your CRM?"
              description="Rate how well your CRM is configured and utilized"
            >
              <PercentDial
                darkMode={darkMode}
                value={formData.crmOptimization || 0}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, crmOptimization: value }));
                  setTimeout(() => saveTechAssessmentData(), 100);
                }}
              />
            </AssessmentPanel>
          </div>

          <AssessmentPanel emoji="🧠" title="Do you have the following?">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <HaveItCard
                emoji="🤖"
                label="Automations"
                info="Automated workflows, email sequences, or other business process automations that help streamline your operations."
                selected={formData.hasAutomations === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    hasAutomations: prev.hasAutomations === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveTechAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="✨"
                label="AI Tools"
                info="Artificial intelligence tools and platforms that help automate tasks, analyze data, or enhance decision-making processes."
                selected={formData.hasAITools === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    hasAITools: prev.hasAITools === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveTechAssessmentData(), 100);
                }}
              />
              <HaveItCard
                emoji="🔌"
                label="Website Plugins"
                info="Custom functionality or third-party plugins built specifically for your website to extend its capabilities."
                selected={formData.hasWebsitePlugins === 'yes'}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    hasWebsitePlugins: prev.hasWebsitePlugins === 'yes' ? 'no' : 'yes',
                  }));
                  setTimeout(() => saveTechAssessmentData(), 100);
                }}
              />
            </div>
          </AssessmentPanel>

          {onboardingProgress?.techAssessment && (
            <CompletedNote>Technology Assessment Completed!</CompletedNote>
          )}
        </div>
      );
    }
    
    // Default star rating for non-onboarding mode
    return (
      <div>
        <StarRating
          rating={formData.techRating}
          onRatingChange={(rating) => setFormData(prev => ({ ...prev, techRating: rating }))}
          title="Rate Your Technology Usage"
        />
      </div>
    );
  };

  const renderCurrentSection = () => {
    // Use different sections based on contractor type
    if (formData.contractorType === 'Subcontractor') {
      switch (currentSection) {
        case 0:
          return renderContractorType();
        case 1:
          return renderSubcontractorFocus();
        case 2:
          return renderSalesRating();
        case 3:
          return renderOperationsRating();
        case 4:
          return renderFinanceRating();
        case 5:
          return renderMarketingRating();
        case 6:
          return renderTechRating();
        default:
          return renderContractorType();
      }
    } else if (formData.contractorType === 'Home Improvement') {
      switch (currentSection) {
        case 0:
          return renderContractorType();
        case 1:
          return renderClientTypes();
        case 2:
          return renderHomeImprovementFocus();
        case 3:
          return renderSalesRating();
        case 4:
          return renderOperationsRating();
        case 5:
          return renderFinanceRating();
        case 6:
          return renderMarketingRating();
        case 7:
          return renderTechRating();
        default:
          return renderContractorType();
      }
    } else {
      // General Contractor flow (full assessment)
      switch (currentSection) {
        case 0:
          return renderContractorType();
        case 1:
          return renderProjectType();
        case 2:
          return renderSalesRating();
        case 3:
          return renderOperationsRating();
        case 4:
          return renderFinanceRating();
        case 5:
          return renderMarketingRating();
        case 6:
          return renderTechRating();
        default:
          return renderContractorType();
      }
    }
  };

  // Get current sections array based on contractor type
  const getCurrentSections = () => {
    if (formData.contractorType === 'Subcontractor') {
      return subcontractorSections;
    } else if (formData.contractorType === 'Home Improvement') {
      return homeImprovementSections;
    } else {
      return sections;
    }
  };

  // Title + emoji for the step the lead is on — drives the progress chip and the
  // card header so they always agree.
  const currentSectionMeta = getCurrentSections()[currentSection];

  return (
    <div className="min-h-screen relative">
      {/* Same ambient BBA background the Start Dashboard shows */}
      <PortalBackground />
      <StartSidebar />
      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
          
          /* Slider Styling */
          input[type="range"] {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 8px;
            border-radius: 4px;
            outline: none;
          }
          
          input[type="range"]::-webkit-slider-runnable-track {
            width: 100%;
            height: 8px;
            background: ${darkMode ? '#374151' : '#d1d5db'};
            border-radius: 4px;
          }
          
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            background: #ef4444;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            cursor: pointer;
            margin-top: -6px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            border: 2px solid ${darkMode ? '#1f2937' : '#ffffff'};
            position: relative;
            z-index: 1;
          }
          
          input[type="range"]::-webkit-slider-thumb:hover {
            background: #dc2626;
            transform: scale(1.1);
          }
          
          input[type="range"]::-moz-range-track {
            width: 100%;
            height: 8px;
            background: ${darkMode ? '#374151' : '#d1d5db'};
            border-radius: 4px;
            border: none;
          }
          
          input[type="range"]::-moz-range-thumb {
            background: #ef4444;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid ${darkMode ? '#1f2937' : '#ffffff'};
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
          
          input[type="range"]::-moz-range-thumb:hover {
            background: #dc2626;
            transform: scale(1.1);
          }
          
          input[type="range"]::-ms-track {
            width: 100%;
            height: 8px;
            background: transparent;
            border-color: transparent;
            color: transparent;
          }
          
          input[type="range"]::-ms-fill-lower {
            background: ${darkMode ? '#374151' : '#d1d5db'};
            border-radius: 4px;
          }
          
          input[type="range"]::-ms-fill-upper {
            background: ${darkMode ? '#374151' : '#d1d5db'};
            border-radius: 4px;
          }
          
          input[type="range"]::-ms-thumb {
            background: #ef4444;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid ${darkMode ? '#1f2937' : '#ffffff'};
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Page header */}
        <header className="text-center">
          <div className="flex justify-center mb-3">
            <img src="/bba-emblem.svg" alt="BBA" className="h-12 md:h-14 w-auto" />
          </div>
          <h1 className={`text-3xl md:text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Business Assessment
          </h1>
        </header>

        {/* Step counter + progress bar */}
        <AssessmentProgress
          step={currentSection + 1}
          total={getCurrentSections().length}
          percent={progress}
          sectionTitle={currentSectionMeta?.title || 'Assessment'}
          sectionEmoji={currentSectionMeta?.emoji || '📋'}
        />

        {/* Main Form */}
        <div
          ref={mainContentRef}
          className="overflow-hidden rounded-2xl border border-gray-200 bg-white/85 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/85"
        >
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              {currentSectionMeta?.title || 'Assessment'}
            </h2>
          </div>

          <div className="p-5 sm:p-8">
            {renderCurrentSection()}

            {/* Navigation Buttons */}
            <div className={`flex flex-wrap justify-between items-center gap-3 mt-10 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={prevSection}
                disabled={currentSection === 0}
                className={`inline-flex min-h-[44px] items-center rounded-xl border-2 px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  currentSection === 0
                    ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600'
                    : 'border-gray-200 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-red-300 hover:text-red-600 hover:shadow-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-red-500/60'
                }`}
              >
                <FaArrowLeft className="mr-2" />
                Back
              </button>

              {currentSection === getCurrentSections().length - 1 ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={
                    progress < 30 ||
                    formData.techRating === 0 ||
                    !formData.whatCRM || String(formData.whatCRM).trim() === '' ||
                    formData.crmOptimization === undefined || formData.crmOptimization === null || formData.crmOptimization === 0
                  }
                  className={`inline-flex min-h-[44px] items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold shadow-sm transition-all duration-200 ${
                    progress >= 30 &&
                    formData.techRating !== 0 &&
                    formData.whatCRM && String(formData.whatCRM).trim() !== '' &&
                    formData.crmOptimization !== undefined && formData.crmOptimization !== null && formData.crmOptimization !== 0
                      ? 'bg-red-600 text-white shadow-red-600/25 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg'
                      : 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                  }`}
                >
                  <span aria-hidden="true">🎉</span>
                  Complete Assessment
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={
                    (currentSection === 0 && !formData.contractorType) ||
                    (currentSection === 1 && formData.contractorType === 'Home Improvement' && !formData.clientTypes) ||
                    (currentSection === 1 && formData.contractorType === 'General Contractor' && (!formData.projectType || !formData.projectFocusType || formData.projectFocusType.length === 0)) ||
                    (currentSection === 1 && formData.contractorType === 'Subcontractor' && formData.subcontractorFocus.length === 0) ||
                    (currentSection === 2 && formData.contractorType === 'Home Improvement' && formData.homeImprovementFocus.length === 0) ||
                    (currentSection === 2 && formData.contractorType === 'Home Improvement' && (!formData.preferredHomeImprovementFocus || !formData.mostCommonHomeImprovementFocus)) ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && (formData.salesRating === 0 || !revenueData.oneYearAgo || String(revenueData.oneYearAgo).trim() === '' || !revenueData.currentYearProjection || String(revenueData.currentYearProjection).trim() === '' || !revenueData.nextYearGoal || String(revenueData.nextYearGoal).trim() === '') ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.referralBusinessPercentage < 0 ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.repeatCustomerPercentage < 0 ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.closeRatio < 0 ||
                    ((currentSection === 3 && formData.contractorType === 'General Contractor') || (currentSection === 3 && formData.contractorType === 'Subcontractor') || (currentSection === 4 && formData.contractorType === 'Home Improvement')) && (formData.operationsRating === 0 || !formData.teamSize || String(formData.teamSize).trim() === '' || !formData.teamGrade || !formData.cultureGrade) ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && formData.financeRating === 0 ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.unencumberedCash || String(formData.unencumberedCash).trim() === '') ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.grossProfitMargin || String(formData.grossProfitMargin).trim() === '') ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.netProfitMargin || String(formData.netProfitMargin).trim() === '') ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 5 && formData.contractorType === 'Subcontractor') || (currentSection === 6 && formData.contractorType === 'Home Improvement')) && formData.marketingRating === 0 ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 5 && formData.contractorType === 'Subcontractor') || (currentSection === 6 && formData.contractorType === 'Home Improvement')) && !formData.workingWithMarketingAgency ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 6 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.marketingAgencyType ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && formData.techRating === 0 ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && (!formData.whatCRM || String(formData.whatCRM).trim() === '') ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && (formData.crmOptimization === undefined || formData.crmOptimization === null || formData.crmOptimization === 0) ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.biggestHurdle ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.mainGrowthOpportunities ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.additionalComments
                  }
                  className={`inline-flex min-h-[44px] items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold shadow-sm transition-all duration-200 ${
                    (currentSection === 0 && !formData.contractorType) ||
                    (currentSection === 1 && formData.contractorType === 'Home Improvement' && !formData.clientTypes) ||
                    (currentSection === 1 && formData.contractorType === 'General Contractor' && (!formData.projectType || !formData.projectFocusType || formData.projectFocusType.length === 0)) ||
                    (currentSection === 1 && formData.contractorType === 'Subcontractor' && formData.subcontractorFocus.length === 0) ||
                    (currentSection === 2 && formData.contractorType === 'Home Improvement' && formData.homeImprovementFocus.length === 0) ||
                    (currentSection === 2 && formData.contractorType === 'Home Improvement' && (!formData.preferredHomeImprovementFocus || !formData.mostCommonHomeImprovementFocus)) ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && (formData.salesRating === 0 || !revenueData.oneYearAgo || String(revenueData.oneYearAgo).trim() === '' || !revenueData.currentYearProjection || String(revenueData.currentYearProjection).trim() === '' || !revenueData.nextYearGoal || String(revenueData.nextYearGoal).trim() === '') ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.referralBusinessPercentage < 0 ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.repeatCustomerPercentage < 0 ||
                    ((currentSection === 2 && formData.contractorType === 'General Contractor') || (currentSection === 2 && formData.contractorType === 'Subcontractor') || (currentSection === 3 && formData.contractorType === 'Home Improvement')) && formData.closeRatio < 0 ||
                    ((currentSection === 3 && formData.contractorType === 'General Contractor') || (currentSection === 3 && formData.contractorType === 'Subcontractor') || (currentSection === 4 && formData.contractorType === 'Home Improvement')) && (formData.operationsRating === 0 || !formData.teamSize || String(formData.teamSize).trim() === '' || !formData.teamGrade || !formData.cultureGrade) ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && formData.financeRating === 0 ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.unencumberedCash || String(formData.unencumberedCash).trim() === '') ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.grossProfitMargin || String(formData.grossProfitMargin).trim() === '') ||
                    ((currentSection === 4 && formData.contractorType === 'General Contractor') || (currentSection === 4 && formData.contractorType === 'Subcontractor') || (currentSection === 5 && formData.contractorType === 'Home Improvement')) && (!formData.netProfitMargin || String(formData.netProfitMargin).trim() === '') ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 5 && formData.contractorType === 'Subcontractor') || (currentSection === 6 && formData.contractorType === 'Home Improvement')) && formData.marketingRating === 0 ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 5 && formData.contractorType === 'Subcontractor') || (currentSection === 6 && formData.contractorType === 'Home Improvement')) && !formData.workingWithMarketingAgency ||
                    ((currentSection === 5 && formData.contractorType === 'General Contractor') || (currentSection === 6 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.marketingAgencyType ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && formData.techRating === 0 ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && (!formData.whatCRM || String(formData.whatCRM).trim() === '') ||
                    ((currentSection === 6 && formData.contractorType === 'General Contractor') || (currentSection === 6 && formData.contractorType === 'Subcontractor') || (currentSection === 7 && formData.contractorType === 'Home Improvement')) && (formData.crmOptimization === undefined || formData.crmOptimization === null || formData.crmOptimization === 0) ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.biggestHurdle ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.mainGrowthOpportunities ||
                    ((currentSection === 8 && formData.contractorType === 'General Contractor') || (currentSection === 9 && (formData.contractorType === 'Subcontractor' || formData.contractorType === 'Home Improvement'))) && !formData.additionalComments
                      ? 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                      : 'bg-red-600 text-white shadow-red-600/25 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg'
                  }`}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Goals Popup Modal */}
      {showGoalsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGoalsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Set Your Sales Goals
                </h2>
              </div>

              <div className="space-y-6">
                {/* Revenue and Closing Rate Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Revenue Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear() + 1} Revenue
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="text"
                        name="annualRevenueTarget"
                        value={goalsData.annualRevenueTarget}
                        onChange={handleGoalsDataChange}
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() + 1} revenue`}
                      />
                    </div>
                  </div>

                  {/* Closing Rate Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Closing Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="closingRateTarget"
                        value={goalsData.closingRateTarget}
                        onChange={handleGoalsDataChange}
                        min="0"
                        max="100"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter closing rate"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </div>

                {/* Custom Goals Section */}
                <div>
                  {/* Display Custom Goals as Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {goalsData.customGoals.map((goal) => (
                      <div key={goal.id} className={`p-4 rounded-lg border-2 relative ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <button
                          onClick={() => removeCustomGoal(goal.id)}
                          className={`absolute top-2 right-2 text-red-500 hover:text-red-700 dark:hover:text-red-400`}
                        >
                          🗑️
                        </button>
                        <div className="text-center">
                          <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {goal.name}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {goal.target}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* New Goal Card */}
                    {showNewGoalCard && (
                      <div className={`p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-center space-y-3">
                          <input
                            type="text"
                            name="name"
                            value={newGoal.name}
                            onChange={handleNewGoalChange}
                            className="w-full py-2 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                            placeholder="Goal"
                          />
                          <input
                            type="text"
                            name="target"
                            value={newGoal.target}
                            onChange={handleNewGoalChange}
                            className="w-full py-2 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                            placeholder="Target"
                          />
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => {
                                setShowNewGoalCard(false);
                                setNewGoal({ name: '', target: '' });
                              }}
                              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={addCustomGoal}
                              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Goal Plus Button */}
                    {!showNewGoalCard && (
                      <div 
                        className={`p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 ${
                          darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}
                        onClick={showAddGoalCard}
                      >
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="text-4xl text-red-500 mb-2">+</div>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Add Goal
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowGoalsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveGoals}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Goals
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* GPM Popup Modal */}
      {showGPMPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGPMPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Enter Your Gross Profit Margin Data
                </h2>
              </div>

              <div className="space-y-6">
                {/* GPM Input Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {/* 2 Years Ago GPM */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear() - 2} Gross Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="twoYearsAgo"
                        value={gpmData.twoYearsAgo}
                        onChange={handleGpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 2} GPM`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>

                  {/* 1 Year Ago GPM */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear() - 1} Gross Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="oneYearAgo"
                        value={gpmData.oneYearAgo}
                        onChange={handleGpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 1} GPM`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>

                  {/* Current Year Projection */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear()} Gross Profit Margin Projection
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="currentYearProjection"
                        value={gpmData.currentYearProjection}
                        onChange={handleGpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear()} GPM projection`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowGPMPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveGPM}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save GPM Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NPM Popup Modal */}
      {showNPMPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowNPMPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Enter Your Net Profit Margin Data
                </h2>
              </div>

              <div className="space-y-6">
                {/* NPM Input Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {/* 2 Years Ago NPM */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear() - 2} Net Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="twoYearsAgo"
                        value={npmData.twoYearsAgo}
                        onChange={handleNpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 2} NPM`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>

                  {/* 1 Year Ago NPM */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear() - 1} Net Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="oneYearAgo"
                        value={npmData.oneYearAgo}
                        onChange={handleNpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 1} NPM`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>

                  {/* Current Year Projection */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().getFullYear()} Net Profit Margin Projection
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="currentYearProjection"
                        value={npmData.currentYearProjection}
                        onChange={handleNpmDataChange}
                        min="0"
                        max="100"
                        step="0.1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear()} NPM projection`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowNPMPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNPM}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save NPM Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leads Popup Modal */}
      {showLeadsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLeadsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Enter Your Leads Data
                </h2>
              </div>

              <div className="space-y-6">
                {/* Leads Input Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {/* 2 Months Ago Leads */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date(new Date().setMonth(new Date().getMonth() - 2)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Leads
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="twoYearsAgo"
                        value={leadsData.twoYearsAgo}
                        onChange={handleLeadsDataChange}
                        min="0"
                        step="1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date(new Date().setMonth(new Date().getMonth() - 2)).toLocaleDateString('en-US', { month: 'long' })} leads`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">leads</span>
                    </div>
                  </div>

                  {/* 1 Month Ago Leads */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Leads
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="oneYearAgo"
                        value={leadsData.oneYearAgo}
                        onChange={handleLeadsDataChange}
                        min="0"
                        step="1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleDateString('en-US', { month: 'long' })} leads`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">leads</span>
                    </div>
                  </div>

                  {/* Current Month Projection */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Leads Projection
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="currentYearProjection"
                        value={leadsData.currentYearProjection}
                        onChange={handleLeadsDataChange}
                        min="0"
                        step="1"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().toLocaleDateString('en-US', { month: 'long' })} leads projection`}
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">leads</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowLeadsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveLeads}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Leads Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tech Stack Popup Modal */}
      {showTechStackPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTechStackPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Enter Your Tech Stack Information
                </h2>
              </div>

              <div className="space-y-6">
                {/* CRM Input Card */}
                <div className={`p-4 rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Customer Relationship Management (CRM) System
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="crm"
                      value={techStackData.crm}
                      onChange={handleTechStackDataChange}
                      className="py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500 text-center"
                      placeholder="e.g. JobTread, Buildertrend, None"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowTechStackPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTechStack}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Tech Stack Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mission Vision Values Popup Modal */}
      {showMVVPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMVVPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mission, Vision & Values
                </h2>
              </div>

              <div className="space-y-6">
                {/* Mission Input */}
                <div className={`p-4 rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Mission Statement
                  </label>
                  <textarea
                    name="mission"
                    value={mvvData.mission}
                    onChange={handleMVVDataChange}
                    rows={3}
                    className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500 resize-none"
                    placeholder="Enter your company's mission statement..."
                  />
                </div>

                {/* Vision Input */}
                <div className={`p-4 rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Vision Statement
                  </label>
                  <textarea
                    name="vision"
                    value={mvvData.vision}
                    onChange={handleMVVDataChange}
                    rows={3}
                    className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500 resize-none"
                    placeholder="Enter your company's vision statement..."
                  />
                </div>

                {/* Values Section */}
                <div className={`p-4 rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Company Values
                  </label>
                  
                  {/* Add Value Input */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={newValue}
                      onChange={handleNewValueChange}
                      placeholder="Enter a company value..."
                      className="flex-1 py-2 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      onKeyPress={(e) => e.key === 'Enter' && addValue()}
                    />
                    <button
                      onClick={addValue}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>

                  {/* Values List */}
                  <div className="space-y-2">
                    {mvvData.values.map((value) => (
                      <div key={value.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500">
                        <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {value.text}
                        </span>
                        <button
                          onClick={() => removeValue(value.id)}
                          className="text-red-500 hover:text-red-700 dark:hover:text-red-400 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {mvvData.values.length === 0 && (
                      <p className={`text-sm text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No values added yet. Add your first company value above.
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowMVVPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveMVV}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save MVV Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Org Chart Popup Modal */}
      {showOrgChartPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowOrgChartPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Organization Chart - Employee Directory
                </h2>
              </div>

              <div className="space-y-6">
                {/* Add New Employee Form */}
                <div className={`p-3 rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <h3 className={`text-base font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Add New Employee
                  </h3>
                  <div className="space-y-3">
                    {/* First Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      <input
                        type="text"
                        name="name"
                        value={newEmployee.name}
                        onChange={handleNewEmployeeChange}
                        placeholder="Name"
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      />
                      <input
                        type="text"
                        name="title"
                        value={newEmployee.title}
                        onChange={handleNewEmployeeChange}
                        placeholder="Title"
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      />
                      <select
                        name="reportsTo"
                        value={newEmployee.reportsTo}
                        onChange={handleNewEmployeeChange}
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      >
                        <option value="">Reports To</option>
                        {orgChartData.employees.map((emp) => (
                          <option key={emp.id} value={emp.title}>
                            {emp.title}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        name="tenure"
                        value={newEmployee.tenure}
                        onChange={handleNewEmployeeChange}
                        placeholder="Tenure"
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      />
                    </div>
                    {/* Second Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      <select
                        name="grade"
                        value={newEmployee.grade}
                        onChange={handleNewEmployeeChange}
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      >
                        <option value="A">Grade A</option>
                        <option value="B">Grade B</option>
                        <option value="C">Grade C</option>
                        <option value="D">Grade D</option>
                        <option value="E">Grade E</option>
                      </select>
                      <input
                        type="text"
                        name="salary"
                        value={newEmployee.salary}
                        onChange={handleNewEmployeeChange}
                        placeholder="Salary"
                        className="py-1.5 px-2 rounded border border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                      />
                      <button
                        onClick={addEmployee}
                        className="py-1.5 px-4 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-colors duration-200"
                      >
                        Add Employee
                      </button>
                    </div>
                  </div>
                </div>

                {/* Employee Table */}
                <div className={`rounded-lg border-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Name</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Title</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Reports To</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Tenure</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Grade</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Salary</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            darkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`${darkMode ? 'divide-gray-600' : 'divide-gray-200'} divide-y`}>
                        {orgChartData.employees.map((employee) => (
                          <tr key={employee.id} className={`${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}`}>
                            {editingEmployee && editingEmployee.id === employee.id ? (
                              <>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    name="name"
                                    value={editingEmployee.name}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    name="title"
                                    value={editingEmployee.title}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <select
                                    name="reportsTo"
                                    value={editingEmployee.reportsTo}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  >
                                    <option value="">Select...</option>
                                    {orgChartData.employees
                                      .filter(emp => emp.id !== editingEmployee.id)
                                      .map((emp) => (
                                        <option key={emp.id} value={emp.title}>
                                          {emp.title}
                                        </option>
                                      ))}
                                  </select>
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    name="tenure"
                                    value={editingEmployee.tenure}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <select
                                    name="grade"
                                    value={editingEmployee.grade}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  >
                                    <option value="A">A</option>
                                    <option value="B">B</option>
                                    <option value="C">C</option>
                                    <option value="D">D</option>
                                    <option value="E">E</option>
                                  </select>
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    name="salary"
                                    value={editingEmployee.salary}
                                    onChange={handleEditEmployeeChange}
                                    className="w-full py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-500 dark:text-white text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={saveEditEmployee}
                                      className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingEmployee(null)}
                                      className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {employee.name}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {employee.title}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {employee.reportsTo}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {employee.tenure}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {employee.grade}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  ${employee.salary}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => editEmployee(employee.id)}
                                      className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => deleteEmployee(employee.id)}
                                      className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        {orgChartData.employees.length === 0 && (
                          <tr>
                            <td colSpan="7" className={`px-4 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              No employees added yet. Add your first employee above.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowOrgChartPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveOrgChart}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Org Chart Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Marketing Goals Popup Modal */}
      {showMarketingGoalsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMarketingGoalsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Set Your Marketing Goals
                </h2>
              </div>

              <div className="space-y-6">
                {/* Monthly Leads and Cost Per Lead Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Monthly Leads Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Monthly Leads
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="monthlyLeadsTarget"
                        value={marketingGoalsData.monthlyLeadsTarget}
                        onChange={handleMarketingGoalsDataChange}
                        min="0"
                        className="py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter monthly leads target"
                      />
                    </div>
                  </div>

                  {/* Cost Per Lead Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Cost Per Lead
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="number"
                        name="costPerLeadTarget"
                        value={marketingGoalsData.costPerLeadTarget}
                        onChange={handleMarketingGoalsDataChange}
                        min="0"
                        step="0.01"
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter cost per lead target"
                      />
                    </div>
                  </div>
                </div>

                {/* Custom Goals Section */}
                <div>
                  {/* Display Custom Goals as Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {marketingGoalsData.customGoals.map((goal) => (
                      <div key={goal.id} className={`p-4 rounded-lg border-2 relative ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <button
                          onClick={() => removeMarketingCustomGoal(goal.id)}
                          className={`absolute top-2 right-2 text-red-500 hover:text-red-700 dark:hover:text-red-400`}
                        >
                          🗑️
                        </button>
                        <div className="text-center">
                          <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {goal.name}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {goal.target}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* New Goal Card */}
                    {showNewGoalCard && (
                      <div className={`p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-center space-y-3">
                          <input
                            type="text"
                            name="name"
                            value={newGoal.name}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Goal name"
                          />
                          <input
                            type="text"
                            name="target"
                            value={newGoal.target}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Target value"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={addMarketingCustomGoal}
                              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => setShowNewGoalCard(false)}
                              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Custom Goal Button */}
                    {!showNewGoalCard && (
                      <button
                        onClick={showAddGoalCard}
                        className={`p-4 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
                          darkMode ? 'border-gray-600 hover:border-blue-400' : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>+</div>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Add Custom Goal
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowMarketingGoalsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveMarketingGoals}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Goals
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finance Goals Popup Modal */}
      {showFinanceGoalsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFinanceGoalsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Set Your Finance Goals
                </h2>
              </div>

              <div className="space-y-6">
                {/* Monthly Overhead and Net Profit Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Monthly Overhead Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Monthly Overhead
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="number"
                        name="monthlyOverheadTarget"
                        value={financeGoalsData.monthlyOverheadTarget}
                        onChange={handleFinanceGoalsDataChange}
                        min="0"
                        step="0.01"
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter monthly overhead target"
                      />
                    </div>
                  </div>

                  {/* Net Profit Margin Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Net Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="netProfitMarginTarget"
                        value={financeGoalsData.netProfitMarginTarget}
                        onChange={handleFinanceGoalsDataChange}
                        min="0"
                        max="100"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter net profit margin target"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </div>

                {/* Custom Goals Section */}
                <div>
                  {/* Display Custom Goals as Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {financeGoalsData.customGoals.map((goal) => (
                      <div key={goal.id} className={`p-4 rounded-lg border-2 relative ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <button
                          onClick={() => removeFinanceCustomGoal(goal.id)}
                          className={`absolute top-2 right-2 text-red-500 hover:text-red-700 dark:hover:text-red-400`}
                        >
                          🗑️
                        </button>
                        <div className="text-center">
                          <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {goal.name}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {goal.target}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* New Goal Card */}
                    {showNewGoalCard && (
                      <div className={`p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-center space-y-3">
                          <input
                            type="text"
                            name="name"
                            value={newGoal.name}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Goal name"
                          />
                          <input
                            type="text"
                            name="target"
                            value={newGoal.target}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Target value"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={addFinanceCustomGoal}
                              className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => setShowNewGoalCard(false)}
                              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Custom Goal Button */}
                    {!showNewGoalCard && (
                      <button
                        onClick={showAddGoalCard}
                        className={`p-4 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 ${
                          darkMode ? 'border-gray-600 hover:border-purple-400' : 'border-gray-300 hover:border-purple-400'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>+</div>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Add Custom Goal
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowFinanceGoalsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveFinanceGoals}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Goals
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tech Goals Popup Modal */}
      {showTechGoalsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTechGoalsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Set Your Technology Goals
                </h2>
              </div>

              <div className="space-y-6">
                {/* Active Automations and Tech Stack Monthly Costs Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Active Automations Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Active Automations
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="activeAutomationsTarget"
                        value={techGoalsData.activeAutomationsTarget}
                        onChange={handleTechGoalsDataChange}
                        min="0"
                        className="py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter active automations target"
                      />
                    </div>
                  </div>

                  {/* Tech Stack Monthly Costs Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Tech Stack Monthly Costs
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="number"
                        name="techStackMonthlyCostsTarget"
                        value={techGoalsData.techStackMonthlyCostsTarget}
                        onChange={handleTechGoalsDataChange}
                        min="0"
                        step="0.01"
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter monthly tech costs target"
                      />
                    </div>
                  </div>
                </div>

                {/* Custom Goals Section */}
                <div>
                  {/* Display Custom Goals as Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {techGoalsData.customGoals.map((goal) => (
                      <div key={goal.id} className={`p-4 rounded-lg border-2 relative ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <button
                          onClick={() => removeTechCustomGoal(goal.id)}
                          className={`absolute top-2 right-2 text-red-500 hover:text-red-700 dark:hover:text-red-400`}
                        >
                          🗑️
                        </button>
                        <div className="text-center">
                          <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {goal.name}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {goal.target}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* New Goal Card */}
                    {showNewGoalCard && (
                      <div className={`p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-center space-y-3">
                          <input
                            type="text"
                            name="name"
                            value={newGoal.name}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Goal name"
                          />
                          <input
                            type="text"
                            name="target"
                            value={newGoal.target}
                            onChange={handleNewGoalChange}
                            className="py-2 px-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder="Target value"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={addTechCustomGoal}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => setShowNewGoalCard(false)}
                              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Custom Goal Button */}
                    {!showNewGoalCard && (
                      <button
                        onClick={showAddGoalCard}
                        className={`p-4 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 ${
                          darkMode ? 'border-gray-600 hover:border-green-400' : 'border-gray-300 hover:border-green-400'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>+</div>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Add Custom Goal
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowTechGoalsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTechGoals}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Goals
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operations Goals Popup Modal */}
      {showOperationsGoalsPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowOperationsGoalsPopup(false);
            }
          }}
        >
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'border border-gray-700' : 'border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Set Your Operations Goals
                </h2>
              </div>

              <div className="space-y-6">
                {/* Gross Profit Margin and Slippage % Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Gross Profit Margin Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gross Profit Margin
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="grossProfitMarginTarget"
                        value={operationsGoalsData.grossProfitMarginTarget}
                        onChange={handleOperationsGoalsDataChange}
                        min="0"
                        max="100"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter GPM target"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>

                  {/* Slippage % Card */}
                  <div className={`p-4 rounded-lg border-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <label className={`block text-sm font-medium mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Slippage %
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="slippagePercentageTarget"
                        value={operationsGoalsData.slippagePercentageTarget}
                        onChange={handleOperationsGoalsDataChange}
                        min="0"
                        max="100"
                        className="pr-8 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter slippage target"
                      />
                      <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  </div>
                </div>

                {/* Custom Goals Section */}
                <div>
                  {/* Display Custom Goals as Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {operationsGoalsData.customGoals.map((goal) => (
                      <div key={goal.id} className={`p-4 rounded-lg border-2 relative ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <button
                          onClick={() => removeOperationsCustomGoal(goal.id)}
                          className={`absolute top-2 right-2 text-red-500 hover:text-red-700 dark:hover:text-red-400`}
                        >
                          🗑️
                        </button>
                        <div className="text-center">
                          <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {goal.name}
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {goal.target}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* New Goal Card */}
                    {showNewGoalCard && (
                      <div className={`p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-center space-y-3">
                          <input
                            type="text"
                            name="name"
                            value={newGoal.name}
                            onChange={handleOperationsNewGoalChange}
                            className="w-full py-2 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                            placeholder="Goal"
                          />
                          <input
                            type="text"
                            name="target"
                            value={newGoal.target}
                            onChange={handleOperationsNewGoalChange}
                            className="w-full py-2 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-800 dark:bg-gray-600 dark:text-white text-sm transition-all duration-200"
                            placeholder="Target"
                          />
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => {
                                setShowNewGoalCard(false);
                                setNewGoal({ name: '', target: '' });
                              }}
                              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={addOperationsCustomGoal}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Goal Plus Button */}
                    {!showNewGoalCard && (
                      <div 
                        className={`p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 ${
                          darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}
                        onClick={showAddGoalCard}
                      >
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="text-4xl text-green-500 mb-2">+</div>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Add Goal
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    onClick={() => setShowOperationsGoalsPopup(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveOperationsGoals}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Goals
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExistingConditions; 